/* eslint-disable import/no-anonymous-default-export */
// Generated by npm run sync-manual. Do not edit directly.
export default `# Manual de usuario de aivoice

**Plataforma web para agencias automotrices**  
**Idioma:** Español (México)  
**Audiencia:** gerentes, personal de BDC, asesores y administradores de la agencia

> Este manual describe las pantallas disponibles en la versión actual de aivoice. Algunas opciones pueden no aparecer en su cuenta debido a su rol o a la configuración de su agencia. Las funciones marcadas como **(por confirmar)** dependen de una configuración específica de su agencia y pueden variar.

![Vista general de aivoice con el menú lateral abierto](manual-img/01-vista-general.png)
*Vista general de aivoice: menú lateral con los módulos principales y el Panel de inicio.*

## Índice

1. [Antes de comenzar](#1-antes-de-comenzar)
2. [Acceso, registro y recuperación de contraseña](#2-acceso-registro-y-recuperacion-de-contrasena)
3. [Navegación general](#3-navegacion-general)
4. [Panel](#4-panel)
5. [Campañas](#5-campanas)
6. [Agentes](#6-agentes)
7. [Llamadas](#7-llamadas)
8. [Historial y transcripciones](#8-historial-y-transcripciones)
9. [WhatsApp](#9-whatsapp)
10. [Postventa](#10-postventa)
11. [Analítica](#11-analitica)
12. [Calendario](#12-calendario)
13. [Teléfonos](#13-telefonos)
14. [Equipo y configuración de la agencia](#14-equipo-y-configuracion-de-la-agencia)
15. [Herramientas personalizadas](#15-herramientas-personalizadas)
16. [Preguntas frecuentes](#16-preguntas-frecuentes)
17. [Errores comunes y cómo resolverlos](#17-errores-comunes-y-como-resolverlos)
18. [Glosario breve](#18-glosario-breve)

---

## 1. Antes de comenzar

### ¿Qué es aivoice?

aivoice es una plataforma para administrar agentes de voz con inteligencia artificial, realizar llamadas individuales o por campaña, revisar resultados y transcripciones, atender conversaciones de WhatsApp, consultar el calendario de citas y dar seguimiento a clientes de postventa.

### Cómo usar este manual

Este manual está disponible dentro de la plataforma, en el menú lateral, sección **Administrar > Documentación**.

1. Abra **Documentación**.
2. Use el cuadro **Buscar en el manual** para localizar una palabra, por ejemplo “plantilla” o “reintentos”. La búsqueda ignora acentos y mayúsculas.
3. Debajo del cuadro se indica cuántas secciones coinciden; las coincidencias aparecen resaltadas en el texto.
4. Seleccione la **X** del cuadro (**Limpiar búsqueda**) para volver a ver el manual completo.
5. Use la lista **Contenido** para saltar directamente a una sección.

> Si aparece **No encontramos coincidencias**, pruebe con otra palabra o con un término más corto.

<!-- CAPTURA: página Documentación (menú Administrar > Documentación) con el cuadro de búsqueda, el contador de secciones encontradas y la lista Contenido a la izquierda -->

![Página Documentación con búsqueda, contador de secciones y lista Contenido](manual-img/27-documentacion-busqueda.png)
*Página Documentación: cuadro de búsqueda, contador de secciones encontradas y lista Contenido.*

### Roles y permisos

Las funciones visibles dependen del rol asignado a cada persona:

| Rol | Uso habitual | Acceso general |
|---|---|---|
| Operador | Asesor o personal BDC | Panel, campañas, llamadas, historial, analítica, WhatsApp (conversaciones), calendario y documentación. |
| Gerente | Responsable de equipo o BDC | Lo anterior, además de agentes (crear agentes hijos y editar), postventa y administración de operadores en Equipo. |
| Administrador | Responsable de la cuenta | Acceso operativo completo, creación y eliminación de agentes, teléfonos, configuración de WhatsApp, envío de recordatorios de Postventa y administración completa del equipo. |
| Super Admin | Administración de varias empresas | Acceso global, empresas y configuraciones avanzadas. |

> Si una opción no aparece, no necesariamente es un error. Puede estar restringida por el rol o desactivada para la agencia. Por ejemplo, Postventa no se muestra en organizaciones configuradas únicamente para cobranza. En la barra superior puede ver el rol con el que inició sesión.

### Recomendaciones de uso

- Use Google Chrome, Microsoft Edge o un navegador moderno.
- Permita el acceso al micrófono si realizará una llamada desde el navegador.
- Capture los teléfonos con código de país. Para México, utilice el formato \`+52\` seguido del número.
- Antes de lanzar una campaña, confirme agente, número de origen, contactos, horario y reintentos.
- No active **Recordar mis credenciales** en una computadora compartida.

---

## 2. Acceso, registro y recuperación de contraseña

### 2.1 Pantalla de inicio de sesión

**Qué es:** la puerta de entrada a la plataforma.

**Para qué sirve:** permite ingresar con correo y contraseña o con Google, cuando esa opción está configurada.

![Pantalla de inicio de sesión](manual-img/02-inicio-sesion.png)
*Pantalla de inicio de sesión con los campos Correo, Contraseña y el botón Iniciar Sesión.*

#### Cómo iniciar sesión

1. Abra la dirección web de aivoice proporcionada por su agencia.
2. En la pestaña **Iniciar Sesión**, escriba su correo.
3. Capture su contraseña.
4. Si es un equipo personal, puede marcar **Recordar mis credenciales**.
5. Seleccione **Iniciar Sesión**.
6. Si su organización utiliza Google, también puede elegir **Continuar con Google** y seleccionar su cuenta autorizada.

#### Cómo mostrar u ocultar la contraseña

1. Localice el ícono de ojo dentro del campo **Contraseña**.
2. Selecciónelo para mostrar temporalmente la contraseña.
3. Selecciónelo de nuevo para ocultarla.

### 2.2 Registro de una cuenta nueva

**Qué es:** la pestaña **Registrarse** de la pantalla de acceso.

**Para qué sirve:** crea una empresa y su primera cuenta administrativa. En una agencia ya configurada, lo normal es entrar mediante una invitación del administrador.

#### Cómo registrar una empresa

1. Seleccione la pestaña **Registrarse**.
2. Capture el **Nombre de Empresa**.
3. Seleccione la industria más cercana a su operación.
4. Capture su nombre completo y correo.
5. Cree una contraseña de al menos ocho caracteres.
6. Repita la contraseña.
7. Seleccione **Crear Cuenta**.

### 2.3 Invitación al equipo

**Qué es:** una página especial que se abre desde el enlace enviado por un administrador o gerente.

**Para qué sirve:** activa su usuario dentro de la empresa correcta y con el rol asignado.

![Página de invitación con nombre de empresa, nombre, correo, rol y opciones para completar el registro](manual-img/03-invitacion-equipo.png)
*Página de invitación: muestra la empresa, su nombre, correo y rol asignado, además de las opciones para continuar con Google o crear una contraseña y completar el registro.*

#### Cómo aceptar una invitación

1. Abra el enlace de invitación recibido.
2. Verifique el nombre de la empresa, su correo y el rol asignado.
3. Continúe con Google, si está disponible, o cree una contraseña.
4. Confirme la contraseña.
5. Seleccione **Completar Registro**.
6. La plataforma lo dirigirá al Panel.

> Si aparece **Invitación inválida o expirada**, solicite al administrador una invitación nueva.

### 2.4 Recuperación de contraseña

**Para qué sirve:** permite crear una contraseña nueva cuando olvidó la actual, mediante un enlace enviado a su correo.

#### Cómo solicitar un enlace de recuperación

1. En la pantalla de acceso, seleccione **¿Olvidaste tu contraseña?**.
2. Capture su correo.
3. Seleccione **Enviar Enlace**.
4. Revise la bandeja de entrada y la carpeta de correo no deseado.
5. Abra el enlace recibido.
6. Capture y confirme una contraseña nueva de al menos ocho caracteres.
7. Seleccione **Restablecer Contraseña**.
8. Regrese al inicio de sesión e ingrese con la nueva contraseña.

![Pantalla Restablecer contraseña](manual-img/04-restablecer-contrasena.png)
*Pantalla Restablecer contraseña con el campo de correo y el botón Enviar Enlace.*

---

## 3. Navegación general

### Menú lateral

El menú se divide en dos grupos:

- **Principal:** Panel, Agentes, Campañas, Llamadas, Historial, Analítica y Postventa.
- **Administrar:** Teléfonos, WhatsApp, Calendario, Equipo y Documentación (este manual).

En computadora puede contraer el menú con la flecha ubicada en su parte superior. En celular o tableta, ábralo con el botón de menú de la barra superior.

### Barra superior

La barra superior muestra:

- El nombre de la sección actual.
- La empresa a la que pertenece.
- El control de modo claro u oscuro.
- El selector **ES/EN** para cambiar el idioma.
- La campana de notificaciones de leads.
- Su nombre, rol y la opción para cerrar sesión.

![Barra superior](manual-img/05-barra-superior.png)
*Barra superior: empresa, modo claro/oscuro, idioma ES/EN, notificaciones y cierre de sesión.*

### Cómo abrir una notificación de lead

1. Seleccione la campana.
2. Revise las notificaciones recientes.
3. Seleccione una notificación.
4. aivoice abrirá la campaña y resaltará el contacto relacionado cuando sea posible.
5. Puede descartar una notificación o usar **Limpiar todo**.

### Acerca de “Configuración”

La versión actual no tiene una sola pantalla general llamada **Configuración**. Los ajustes están distribuidos así:

- **WhatsApp > Configuración:** mensajes automáticos de Ventas y Postventa, modo piloto de Postventa, respondedor de IA, conexión y plantilla de respaldo.
- **Teléfonos:** números de origen y agente para llamadas entrantes.
- **Equipo:** usuarios, Google Calendar, ubicaciones de postventa y datos de la empresa.
- **Agentes:** voz, instrucciones, transferencias, documentos e integraciones de cada agente.

---

## 4. Panel

**Qué es:** la pantalla principal después de iniciar sesión.

**Para qué sirve:** ofrece un resumen rápido de la operación y accesos directos a las tareas más frecuentes.

![Panel principal](manual-img/06-panel.png)
*Panel con indicadores generales, agentes recientes y llamadas recientes.*

### Información disponible

- Total de agentes.
- Agentes activos.
- Total de llamadas.
- Llamadas activas.
- Hasta cinco agentes recientes.
- Hasta cinco llamadas recientes, con número, fecha y estado.
- Acciones rápidas para crear un agente, hacer una llamada o abrir Analítica.

### Cómo revisar la actividad general

1. Entre a **Panel**.
2. Revise los cuatro indicadores superiores.
3. Consulte **Agentes Recientes** para confirmar cuáles están activos.
4. Consulte **Llamadas Recientes** para identificar llamadas en curso o terminadas.
5. Seleccione **Ver todo** en el bloque que desee explorar.

> Los totales del Panel son un resumen general. Para filtros por fecha y resultados comerciales, utilice **Historial** o **Analítica**.

---

## 5. Campañas

### 5.1 Lista de campañas

**Qué es:** el listado de campañas de llamadas salientes.

**Para qué sirve:** permite crear campañas, localizarlas, revisar su avance y abrir sus detalles.

![Lista de Campañas](manual-img/07-lista-campanas.png)
*Lista de Campañas con buscador, filtros, estado, progreso y acciones.*

#### Estados de una campaña

| Estado | Significado |
|---|---|
| Borrador | La campaña existe, pero aún no está llamando. |
| Programada | Quedó preparada para una fecha y hora futuras. |
| En ejecución | Está procesando contactos dentro del horario permitido. |
| Pausada | Conserva el avance, pero temporalmente no genera llamadas. |
| Completada | Terminó de procesar los contactos y reintentos previstos. |
| Fallida | Se presentó un problema que impidió continuar. |

#### Cómo buscar una campaña

1. Abra **Campañas**.
2. Use las pestañas **Todas**, **Agente** u **Organización** para cambiar el alcance. **Agente** muestra sus campañas individuales; **Organización**, las compartidas con toda la agencia.
3. Escriba el nombre de la campaña en el buscador.
4. Si lo necesita, seleccione un estado.
5. Seleccione el nombre de la campaña o el ícono de ojo para abrirla.

### 5.2 Cómo crear una campaña

La creación se realiza en cuatro pasos: información de campaña, contactos, programación y revisión. Use **Anterior** y **Siguiente** para moverse entre pasos; la campaña no se crea hasta que la lance en el paso 4.

#### Paso 1. Información de campaña

1. En **Campañas**, seleccione **Nueva Campaña**.
2. Capture un nombre fácil de reconocer, por ejemplo: “Seguimiento SUV agosto”.
3. Agregue una descripción breve.
4. Si aparece el campo **Objetivo de campaña**, describa el resultado esperado. El reporte de la llamada se enfocará en ese objetivo.
5. Seleccione el agente que realizará las llamadas.
6. Agregue uno o varios números de origen.
7. Si el agente tiene voz secundaria, decida si desea activar **Cambio de Género**.
8. Elija la visibilidad:
   - **Individual:** las notificaciones se enfocan en el creador y administradores.
   - **Organización:** la campaña y sus notificaciones se comparten con la organización.
9. Seleccione **Siguiente**.

![Paso 1 del asistente de campaña](manual-img/08-campana-paso1.png)
*Paso 1: información de campaña con nombre, descripción, agente y número de origen.*

#### Paso 2. Cargar contactos

Puede usar archivo, pegar una lista o utilizar una integración.

#### Cómo importar contactos desde Excel o CSV

1. Seleccione **Subir archivo**.
2. Arrastre el archivo al recuadro o haga clic para buscarlo.
3. Elija un archivo \`.xlsx\`, \`.xls\` o \`.csv\`. La pantalla también acepta \`.json\`.
4. Espere la validación automática.
5. Revise los conteos de números válidos, inválidos y duplicados o compartidos.
6. Revise la vista previa de los primeros contactos.
7. Corrija el archivo si no se encontró ningún contacto válido.
8. Si el archivo no tiene columna de nombre, la pantalla avisa cuántos contactos se importarán sin nombre; el agente los saludará sin nombre. Puede regresar y corregir el archivo o continuar.
9. Seleccione **Siguiente** cuando exista al menos un contacto válido.

**Columnas recomendadas:**

- \`phone\` o \`telefono\`: obligatoria; contiene el número.
- \`name\` o \`nombre\`: opcional; contiene el nombre del cliente.
- \`external_id\`: opcional; identificador de su sistema.
- Cualquier columna adicional se usa como contexto para que el agente personalice la conversación.

> Use encabezados en la primera fila. No combine celdas. Procure que los teléfonos incluyan código de país y no tengan fórmulas.

> **Tamaño de la lista.** Una sola carga admite desde unas decenas hasta cientos de números (por ejemplo, listas de 50 a 1,000 contactos) sin dividir el archivo. El límite técnico por carga es de **10,000 contactos** y **10 MB** por archivo; si lo excede, la plataforma rechaza la carga con un mensaje de error (en inglés) y deberá dividir la lista. Los números repetidos dentro del archivo se cuentan como duplicados y se cargan una sola vez.

![Carga de contactos con validación](manual-img/09-campana-carga-archivo.png)
*Carga de archivo con conteos de números válidos, inválidos y duplicados, y vista previa.*

#### Cómo importar una lista pegada

1. Seleccione **Pegar lista**.
2. Pegue un teléfono por línea.
3. Seleccione **Validar números**.
4. Revise los resultados.
5. Continúe cuando haya números válidos.

#### Cómo importar desde una integración

1. Seleccione **Integración**.
2. Elija una conexión guardada o pegue la dirección proporcionada por su responsable de datos.
3. Seleccione **Obtener contactos**.
4. Como alternativa, pegue el bloque de datos en **Pegar JSON** y seleccione **Validar números**.
5. Revise el total y la vista previa antes de continuar.

#### Diccionario de datos

Cuando el archivo contiene columnas adicionales, la pantalla puede mostrar **Diccionario de Datos**.

1. Escriba una explicación breve para cada columna, por ejemplo: “Saldo pendiente del cliente”.
2. Si un nombre viene en un formato especial, seleccione el formato correspondiente.
3. Para reutilizar las explicaciones, seleccione **Guardar como Plantilla**, asigne un nombre y guarde.
4. En otra carga, use **Cargar plantilla** para aplicarlas.

#### Paso 3. Programación y ritmo

1. Defina la hora de inicio y fin permitidas para llamar. Se usa la hora de CDMX.
2. Elija **Iniciar ahora** o **Programar**.
3. Si programa la campaña, capture fecha y hora futuras.
4. Ajuste la **Concurrencia**, es decir, cuántas llamadas pueden ocurrir al mismo tiempo, de 1 a 5.
5. Ajuste las **Llamadas por minuto**, de 1 a 20.
6. Defina los **Reintentos máximos**, de 0 (sin reintentos) a 10.
7. Defina la **Espera entre reintentos**, de 5 a 240 minutos.
8. Defina los **Reintentos máximos por día**, de 1 a 10. Los reintentos restantes pasan a las 9:00 del siguiente día hábil.
9. Revise la duración y el costo estimados.
10. Seleccione **Siguiente**.

![Paso de programación](manual-img/10-campana-programacion.png)
*Programación y ritmo: inicio inmediato o programado, concurrencia, llamadas por minuto y reintentos.*

#### Paso 4. Revisar y lanzar

1. Revise nombre, agente, números de origen y cantidad de contactos.
2. Confirme la fuente de los contactos.
3. Revise fecha de inicio, horario permitido, concurrencia, tasa y reintentos.
4. Lea el aviso que indica si la campaña iniciará de inmediato o en la fecha programada.
5. Seleccione **Lanzar Campaña**.
6. Lea la confirmación final y confirme.

Si eligió inicio inmediato, el estado cambiará a **En ejecución**. Si eligió una fecha futura, cambiará a **Programada**.

> Si su agencia se encuentra en fase piloto de marcación, la campaña sólo llamará a los números autorizados por el administrador de la plataforma; el resto de los contactos se omite y se contabiliza sin realizar la llamada. Consulte con su administrador si observa contactos omitidos sin intento (por confirmar).

### 5.3 Detalle de campaña

**Qué es:** la vista de seguimiento de una campaña específica.

**Para qué sirve:** controlar la ejecución, consultar indicadores, filtrar contactos, ver transcripciones y exportar resultados.

![Detalle de campaña en ejecución](manual-img/11-detalle-campana.png)
*Detalle de una campaña en ejecución: indicadores, barra de progreso y registro de llamadas.*

#### Acciones según el estado

- **Borrador o Programada:** Iniciar, editar o eliminar.
- **En ejecución:** Pausar o detener.
- **Pausada:** Reanudar o detener.
- **Cualquier estado:** agregar leads y exportar, cuando la acción esté permitida.

#### Cómo iniciar, pausar, reanudar o detener

1. Abra la campaña.
2. Seleccione la acción disponible en la parte superior.
3. Espere el mensaje de confirmación.
4. Verifique que la etiqueta de estado haya cambiado.

> **Pausar** conserva la posibilidad de continuar. **Detener** finaliza la ejecución; úselo sólo cuando ya no deba seguir llamando.

#### Cómo agregar contactos a una campaña existente

1. Abra la campaña.
2. Seleccione **Agregar leads**.
3. Elija un archivo \`.csv\`, \`.xlsx\`, \`.xls\` o \`.json\` con las mismas columnas recomendadas del paso 2.
4. Espere el resultado de la carga.
5. La plataforma indicará cuántos contactos nuevos se agregaron y cuántos repetidos se omitieron.

#### Cómo revisar el resultado de un contacto

1. En **Registro de Llamadas**, busque por nombre, teléfono o resultado.
2. Si lo requiere, filtre por estado, resultado o número de intentos. Use **Limpiar todo** para quitar los filtros.
3. Ordene por más recientes, más antiguos, nombre, intentos o estado.
4. Seleccione la fila del contacto.
5. En el panel lateral, revise las pestañas de resumen, transcripción, eventos y WhatsApp disponibles.
6. Cierre el panel con la **X**.

![Panel lateral de detalle de contacto](manual-img/12-panel-contacto.png)
*Panel lateral del contacto con las pestañas Resumen, Transcripción, Eventos y WhatsApp.*

#### Cómo exportar una campaña

1. Abra la campaña.
2. Seleccione **Exportar**.
3. Guarde el archivo de Excel (\`.xlsx\`) generado por el navegador. Contiene el registro de llamadas de la campaña.

> Si aparece **No hay contactos para exportar**, la campaña aún no tiene contactos cargados.

### 5.4 Buzón de voz: qué pasa cuando contesta una contestadora

En las llamadas de campaña, aivoice detecta si quien contesta es una contestadora o buzón de voz (detección automática de máquina, AMD):

1. Cuando la operadora telefónica confirma que contestó un buzón (al terminar el saludo grabado o al escuchar el tono), aivoice **cuelga en ese momento**. El agente no deja mensaje grabado ni intenta conversar con la grabación.
2. La llamada se registra como **Sin respuesta** (estado \`no-answer\`) y queda marcada internamente como buzón de voz. En el detalle de campaña, ese contacto se contabiliza en la tarjeta **Buzón de Voz** (y no en **Sin Respuesta**) mientras su último intento sea esa detección; si un reintento posterior termina con otro resultado, el contacto pasa a contarse según ese nuevo resultado. El resultado del contacto sigue siendo **Sin respuesta**, por lo que los reintentos, la tarjeta **Conectadas**, el WhatsApp de **No contestó / buzón** y la Analítica no cambian.
3. El contacto conserva sus reintentos según la programación de la campaña (paso 3 de la sección 5.2). En las llamadas de **Postventa**, si la agencia tiene configurada la regla de reintento al día siguiente, se hace **un solo reintento al día siguiente**, al inicio del horario permitido (según la configuración de su agencia).
4. Si la agencia tiene activa la regla de WhatsApp **No contestó / buzón** (sección 9.2), el mensaje se envía **una sola vez por contacto y campaña**: el reintento del día siguiente no vuelve a enviarlo aunque de nuevo conteste el buzón.
5. Si una persona real contesta y habla, la llamada continúa con normalidad; la detección no cuelga una llamada en la que ya hubo respuesta humana o una cita agendada.

> Esta detección aplica a las llamadas que marca el sistema de campañas (incluidas las de **Postventa > Llamar** e **Iniciar contacto con vencidos**). Las llamadas web de prueba no la usan. En algunos buzones la operadora no logra identificar la grabación; en ese caso la llamada se comporta como antes y puede quedar como **Sin respuesta** por silencio.

### 5.5 Llamadas entrantes

Además de las campañas salientes, cada número de la agencia puede atender llamadas entrantes con un agente de aivoice. La asignación se hace en **Teléfonos > Agente Entrante** (sección 13). Al recibir una llamada en un número configurado, la persona que llama escucha **Conectando con** y el nombre del agente, y la conversación continúa con ese agente; las llamadas entrantes se consultan en **Historial de Llamadas** con el filtro **Entrantes** (sección 8). Si el número no tiene agente entrante asignado, quien llama escucha un aviso de que el número no está configurado para llamadas entrantes y la llamada se corta.

---

## 6. Agentes

### 6.1 Lista de agentes

**Qué es:** el catálogo de agentes de voz de la empresa.

**Para qué sirve:** consultar su estado, voz, idioma, sensibilidad y relación entre agentes principales e hijos.

![Lista de Agentes](manual-img/13-lista-agentes.png)
*Lista de Agentes con tarjetas de agentes principales e hijos.*

#### Acciones disponibles

- Filtrar por **Todos**, **Mías** u **Organización**. **Mías** muestra los agentes de los que usted es propietario.
- Activar o desactivar un agente.
- Editar o consultar un agente.
- Crear un agente hijo desde un agente principal.
- Eliminar un agente, si su rol lo permite.

> Un agente **hijo** hereda parte de la configuración de un agente principal. Los campos heredados aparecen bloqueados y deben cambiarse desde el agente padre.

### 6.2 Cómo crear un agente

1. Abra **Agentes**.
2. Seleccione **Nuevo Agente**.
3. Elija **Empezar desde Cero** o **Crear Agente Hijo**.
4. Si crea un hijo, seleccione el agente padre.
5. Complete la información básica.
6. Configure voz, idioma e instrucciones.
7. Revise transferencia humana, herramientas, integraciones y propiedad según las necesidades de la agencia.
8. Seleccione **Crear Agente**.

> Los gerentes pueden crear agentes hijos, pero no agentes principales desde cero. Los permisos de edición y eliminación también dependen del rol.

![Ventana para crear agente](manual-img/14-modal-nuevo-agente.png)
*Ventana para elegir entre Empezar desde Cero o Crear Agente Hijo.*

### 6.3 Editor de agente

**Información básica**

- Nombre del agente.
- Tipo: General, Cobranza o Ventas.
- Departamento: **Ventas y Cobranza** o **Postventa**. El departamento determina qué conversaciones de WhatsApp y qué citas se asocian al agente.
- Propietario (vendedor), cuando aplica: aparece como organizador de las citas del agente y habilita el filtro **Mías**.
- Región e idioma.

**Instrucciones**

- Definen cómo debe presentarse, conversar y actuar el agente.
- Puede existir una instrucción específica para llamadas salientes.
- Use lenguaje claro, incluya el objetivo, límites y procedimiento de escalamiento.

**Voz y sensibilidad**

- Voz principal y, opcionalmente, voz secundaria.
- Modelo de voz.
- Idioma.
- Sensibilidad a interrupciones: Receptivo, Balanceado, Conservador o Personalizado.
- Muletillas naturales, si se desea una conversación más espontánea.

**Transferencia humana**

- Nombre del contacto principal.
- Número al que se transferirá al cliente.
- Tiempo de timbrado.
- Mensaje cuando nadie contesta.
- Directorio con contactos adicionales.
- Música de espera, después de guardar el agente.

**Base de conocimiento**

Permite subir documentos que el agente puede consultar durante las llamadas.

#### Cómo agregar un documento

1. Guarde primero el agente si es nuevo.
2. Abra **Base de Conocimiento**.
3. Seleccione **Subir Documento**.
4. Elija un archivo permitido, como \`.txt\`, \`.md\`, \`.csv\` o \`.json\`.
5. Espere el mensaje **Documento subido**.
6. Desde la lista puede descargarlo o eliminarlo.

**Integraciones**

Según la configuración disponible, un agente puede agendar citas en el calendario de la agencia, enviar SMS o usar WhatsApp. Algunas conexiones requieren apoyo del administrador.

#### Cómo editar un agente

1. Abra **Agentes**.
2. Seleccione el ícono de lápiz en la tarjeta.
3. Modifique únicamente los campos necesarios.
4. Seleccione **Actualizar Agente**.
5. Espere el mensaje de confirmación.

#### Cómo activar o desactivar un agente

1. Localice su tarjeta.
2. Seleccione **Activar** o **Desactivar**.
3. Confirme que la etiqueta cambió a **Activo** o **Inactivo**.

> Un agente inactivo no aparecerá como opción disponible en algunos flujos de llamadas o campañas.

### 6.4 Agentes de Ventas: pregunta de prueba de manejo

Cuando un agente de **Ventas** (tipo **Ventas** y departamento **Ventas y Cobranza**) agenda una cita con un cliente, ya sea por llamada o por WhatsApp, pregunta **una sola vez**, en el mismo mensaje en que confirma fecha y hora: **¿Desea que la cita incluya prueba de manejo?**

- La cita se guarda con prueba de manejo sólo si el cliente respondió claramente que sí. Si dijo que no, no respondió o la respuesta no fue clara, se guarda sin prueba de manejo y el agente no insiste.
- En WhatsApp, la confirmación que recibe el cliente agrega **con prueba de manejo incluida** cuando aplica.
- En **Calendario**, la cita de Ventas muestra el sufijo **· Prueba de manejo** en su título (sección 12).
- En el resumen de resultado que se envía al CRM de la agencia (cuando está conectado), la nota incluye **Cita con prueba de manejo.**
- Los agentes de **Postventa** nunca hacen esta pregunta: una cita de servicio se agenda en cuanto el cliente acepta el horario, aunque el agente esté registrado con tipo Ventas.

---

## 7. Llamadas

**Qué es:** la pantalla para realizar llamadas de prueba o llamadas individuales y consultar las sesiones recientes.

**Para qué sirve:** permite hablar con un agente desde el navegador o pedirle al sistema que marque a un teléfono.

![Pantalla Llamadas](manual-img/15-llamadas.png)
*Pantalla Llamadas con las pestañas Llamadas Web y Llamadas Telefónicas.*

### 7.1 Llamada web desde el navegador

Esta modalidad sirve para probar al agente hablando directamente desde la computadora.

#### Cómo iniciar una llamada web

1. Abra **Llamadas**.
2. Seleccione la pestaña de llamada web.
3. Seleccione un agente.
4. Seleccione el micrófono que desea utilizar.
5. Si utiliza datos de prueba proporcionados por su empresa, cargue la conexión correspondiente; de lo contrario, deje esa opción vacía.
6. Seleccione **Iniciar Llamada Web**.
7. Autorice el uso del micrófono si el navegador lo solicita.
8. Espere el estado **Conectando** y comience a hablar.
9. Observe los indicadores **Hablando** y **Escuchando**.
10. Seleccione **Terminar Llamada** al concluir.

> Use audífonos para evitar eco. Si el agente no escucha, revise el micrófono elegido y el permiso del navegador.

### 7.2 Llamada telefónica individual

Esta modalidad hace que el agente marque a un teléfono real desde uno de los números de origen de la agencia.

#### Cómo llamar a un cliente

1. Abra **Llamadas**.
2. Seleccione la pestaña de llamada telefónica.
3. Seleccione el agente.
4. Capture el número de destino con código de país.
5. Seleccione el **Número de Origen**.
6. Si su operación usa una fuente de datos de prueba, selecciónela o pegue la dirección indicada por su administrador.
7. Seleccione **Hacer Llamada**.
8. Espere el mensaje **Llamada iniciada**.
9. Revise la sesión en la lista inferior.

### 7.3 Sesiones y transcripción rápida

La parte inferior muestra las sesiones del tipo de llamada seleccionado, con estado, agente y hora de inicio.

#### Cómo ver una transcripción desde Llamadas

1. Localice la sesión.
2. Seleccione **Ver Transcripción**.
3. Revise identificador, agente, hora de inicio y fin.
4. Lea el resumen, si está disponible.
5. Revise el diálogo ordenado entre **Cliente** y **Agente**.
6. Seleccione **Cerrar**.

> Si la llamada acaba de terminar, el resumen o la transcripción pueden tardar unos momentos en aparecer.

---

## 8. Historial y transcripciones

**Qué es:** el registro consolidado de llamadas, con las más recientes primero.

**Para qué sirve:** localizar una llamada por fecha o teléfono y revisar su resultado, resumen y conversación.

![Historial de Llamadas](manual-img/16-historial.png)
*Historial de Llamadas con filtros de fecha, búsqueda por teléfono y tabla de resultados.*

### Información de la tabla

- Fecha y hora.
- Teléfono.
- Agente.
- Duración.
- Resumen.
- Resultado, por ejemplo: interesado, cita agendada, no contesta, buzón de voz o no interesado.

### Cómo buscar una llamada

1. Abra **Historial**.
2. Seleccione **Hoy**, **Esta semana**, **Este mes** o **Personalizado**.
3. En un rango personalizado, elija las fechas **Desde** y **Hasta**.
4. Si conoce el número, escríbalo en el buscador.
5. Seleccione **Buscar**.
6. Seleccione cualquier fila para abrir el detalle.

### Cómo ver una transcripción

1. Abra **Historial**.
2. Localice la llamada.
3. Seleccione la fila.
4. Revise teléfono, agente, duración, resultado y fecha.
5. Lea el **Resumen de Llamada** y el sentimiento, cuando existan.
6. Desplácese por la sección **Transcripción**. Las intervenciones del cliente y del agente aparecen separadas.
7. Cierre la ventana con la **X** o haciendo clic fuera de ella.

![Detalle de llamada con transcripción](manual-img/17-detalle-llamada.png)
*Detalle de llamada con resumen y burbujas de transcripción.*

---

## 9. WhatsApp

### 9.1 Conversaciones

**Qué es:** la bandeja de conversaciones de WhatsApp de la agencia.

**Para qué sirve:** leer mensajes, responder al cliente, separar conversaciones de Ventas y Postventa y controlar el respondedor de IA por conversación.

![Bandeja de WhatsApp](manual-img/18-whatsapp-conversaciones.png)
*WhatsApp: lista de conversaciones a la izquierda y conversación seleccionada a la derecha.*

#### Filtros disponibles

- Departamento: **Todos**, **Ventas** o **Postventa**.
- Actividad: **Respondidas** o **Todas**.

Una misma persona puede tener conversaciones separadas de Ventas y Postventa, porque cada departamento puede usar su propia línea de WhatsApp. Cada conversación muestra la etiqueta de su departamento; confírmela antes de responder.

#### Cómo abrir una conversación

1. Abra **WhatsApp**.
2. Seleccione el departamento.
3. Elija **Respondidas** o **Todas**.
4. Seleccione el nombre o teléfono del cliente.
5. Revise los mensajes en el panel derecho.

Los mensajes salientes indican si fueron **Auto** (enviados por el respondedor de IA o por una regla automática) o **Manual** (escritos por una persona) y pueden mostrar estos estados:

- **En cola:** pendiente de envío.
- Una palomita: enviado.
- Dos palomitas grises: entregado.
- Dos palomitas azules: leído.
- Ícono de advertencia: falló.

#### Cómo responder un WhatsApp dentro de la ventana de 24 horas

1. Abra la conversación.
2. Confirme que el encabezado diga **Ventana de 24h abierta** junto con la hora en que expira.
3. Escriba el mensaje en el campo inferior.
4. Seleccione **Enviar** o presione \`Enter\`.
5. Para insertar un salto de línea sin enviar, use \`Shift + Enter\`.
6. Verifique el estado del mensaje.

#### Cómo responder fuera de la ventana de 24 horas

Fuera de la ventana sólo puede enviarse una plantilla de respaldo previamente configurada. El respondedor de IA tampoco contesta fuera de la ventana.

1. Abra la conversación.
2. Confirme el aviso **Ventana de 24h cerrada** o **Fuera de ventana de 24h**.
3. Revise la plantilla indicada.
4. Seleccione **Enviar plantilla**.
5. Si aparece **No hay plantilla de respaldo configurada**, solicite a un administrador que configure una plantilla aprobada.

#### Cliente dado de baja

Si aparece **Dado de baja**, aivoice bloquea el envío de mensajes. No intente contactar al cliente por WhatsApp hasta contar con una nueva autorización válida conforme a las políticas de la agencia.

#### Cómo activar o desactivar la IA en una conversación

1. Abra la conversación.
2. Localice el control **IA: Activo/Inactivo** en el encabezado.
3. Selecciónelo para cambiar el estado.
4. Confirme visualmente el nuevo estado.

Al desactivar la IA, la conversación queda disponible para atención manual sin respuestas automáticas del agente en ese hilo.

### 9.2 Configuración de WhatsApp

**Quién puede verla:** administradores y Super Admin.

**Para qué sirve:** administrar los mensajes automáticos de Ventas y Postventa, activar el modo piloto de Postventa, elegir un agente respondedor, configurar la conexión de WhatsApp y establecer una plantilla de respaldo.

La pestaña se organiza en tres bloques, en este orden: **Mensajes automáticos**, **Modo piloto Postventa** y la conexión de WhatsApp (respondedor de IA, datos de la cuenta y plantilla de respaldo). Todos se guardan con el mismo botón **Guardar configuración de WhatsApp**.

![Configuración de WhatsApp](manual-img/19-whatsapp-configuracion.png)
*Pestaña Configuración de WhatsApp con mensajes automáticos, variables y vista previa.*

> Los datos de conexión y los identificadores de plantillas deben ser proporcionados por el responsable de WhatsApp, Twilio o Meta de su organización. No los invente ni cambie sin autorización.

#### Cómo configurar mensajes automáticos de Ventas

Los mensajes automáticos de Ventas se envían por WhatsApp después de una llamada, según el resultado que registró el agente.

1. Active **Activar mensajes automáticos** con el interruptor general. Cuando está apagado, ninguna regla se envía.
2. Seleccione la pestaña **Ventas**.
3. Active las reglas que necesite: **Interesado**, **No interesado** o **No contestó**.
4. Escriba el mensaje de cada regla.
5. Personalícelo con las variables disponibles: \`{nombre}\`, \`{agente}\` y \`{empresa}\`.
6. Revise la **Vista previa**.
7. Seleccione **Guardar configuración de WhatsApp**.

#### Cómo configurar mensajes automáticos de Postventa

Los mensajes de Postventa son recordatorios de servicio. Cada vehículo recibe el mensaje de la prioridad que le corresponde según su historial:

| Regla | Cuándo aplica |
|---|---|
| Prioridad 1A | Primer servicio, cadencia de 6 mil km o 6 meses. |
| Prioridad 1B | Primer servicio, cadencia de 12 mil km o 12 meses. |
| Prioridad 2A | Servicio recurrente cada 6 mil km o 6 meses; el mensaje menciona el último servicio. |
| Prioridad 2B | Servicio recurrente cada 12 mil km o 12 meses; el mensaje menciona el último servicio. |
| Franja | Vehículos de 13 a 96 meses desde la venta; mensaje genérico con promociones. |
| Informativo | Mensajes de confianza para clientes con más de 60 días sin contacto; rota hasta diez mensajes distintos. |

1. Active el interruptor general de mensajes automáticos.
2. Seleccione **Postventa**.
3. Active únicamente las prioridades o franjas que use su agencia. Una regla apagada no envía nada aunque existan vehículos vencidos.
4. Redacte cada mensaje con las variables mostradas: \`{cliente}\`, \`{Consultor}\`, \`{dealer}\`, \`{modelo}\`, \`{anioModelo}\`, \`{VIN}\`, \`{fechaUltser}\` y \`{kmUltimo}\`.
5. Revise la vista previa.
6. Para los mensajes informativos, capture el identificador de plantilla aprobado y el texto correspondiente en cada una de las diez posiciones. Estos mensajes sólo admiten \`{cliente}\`, \`{Consultor}\` y \`{dealer}\`.
7. Una posición sin identificador de plantilla se muestra como **deshabilitado (sin SID)** y no se envía.
8. Seleccione **Guardar configuración de WhatsApp**.

> Los recordatorios de Postventa no se envían solos: una persona debe iniciarlos con el botón **Enviar recordatorios** de la pantalla Postventa (vea la sección 10). Las reglas de esta pestaña definen qué mensaje recibe cada vehículo cuando eso ocurre. Existe un envío programado diario que sólo el administrador de la plataforma puede habilitar; viene apagado de fábrica (por confirmar para su agencia).

<!-- CAPTURA: WhatsApp > Configuración, pestaña Postventa de Mensajes automáticos, mostrando las reglas Prioridad 1A/1B/2A/2B, Franja e Informativo con sus interruptores y variables -->

![Pestaña Postventa de Mensajes automáticos con las reglas Prioridad, Franja e Informativo](manual-img/28-wa-postventa-reglas.png)
*WhatsApp > Configuración, pestaña Postventa: reglas Prioridad 1A/1B/2A/2B, Franja e Informativo con interruptores y variables.*

#### Mensajes de Postventa después de una llamada (plantillas v3)

Además de los recordatorios, la pestaña **Postventa** tiene el bloque **Mensajes post-llamada / confirmación**: mensajes que se envían solos después de una llamada de Postventa según el resultado de la llamada. Cada regla usa una **plantilla aprobada de WhatsApp (SID)**; el cliente recibe la plantilla aprobada y el texto capturado en la pantalla es sólo la nota de respaldo. Una regla sin SID se muestra como **sin SID · no se envía**.

| Regla | Cuándo se envía |
|---|---|
| **Confirmación · cita agendada en la llamada** | El cliente agendó una cita durante la llamada. |
| **Seguimiento · interesado sin cita** | El cliente se interesó pero no agendó. |
| **Neutral · seguimiento sin afirmar cita ni servicio vencido** | Respaldo de seguimiento cuando no conviene afirmar una cita. |
| **Cierre · no interesado** | El cliente no quiere el servicio. |
| **No contestó / buzón** | La llamada no fue contestada o entró a buzón. |
| **Post-llamada (genérico)** | Respaldo cuando el resultado no tiene regla propia. |

Los identificadores de plantilla y su aprobación en Meta los gestiona el responsable de WhatsApp de su organización. Desde octubre de 2026 las plantillas vigentes usan la redacción aprobada por la agencia (trato de usted, sin kilometraje en la unidad):

- **Cita registrada (preconfirmación):** *Hola {nombre} 👋 Le saluda el {agencia}. Su cita de servicio en Chevrolet {sucursal} quedó registrada.* Después, en renglones separados: **🗓️ Fecha**, **🚗 Unidad** (modelo y año), **🛠️ Servicio**, **💲 Costo:** *un asesor de servicio le confirmará el costo aproximado en breve por este medio*, y las indicaciones de llegada: *Le estaremos esperando 10 min antes de su cita; por favor ingrese con las intermitentes encendidas y, si cuenta con birlo de seguridad, déjelo en su portavasos. En todo caso, es requerida su constancia de situación fiscal. Gracias.*
- **Cita confirmada con precio:** *Hola {nombre} 👋 Le confirmamos su cita de servicio en Chevrolet {sucursal}.* Luego **🗓️ Fecha**, **🚗 Unidad**, **🛠️ Servicio**, **💲 Costo aproximado: {monto} MXN con IVA incluido**, **👤 Asesor de servicio: {asesor}**, las mismas indicaciones de llegada y el cierre *Si necesita cambiarla, responda a este mensaje. ¡Gracias!*
- **No contestó:** *Hola {nombre} 👋 Le saluda {asesor} de Chevrolet {sucursal}. Intentamos comunicarnos con usted para agendar el servicio de mantenimiento de su {unidad} y no logramos contactarle. Le llamaremos de nuevo mañana; si lo prefiere, responda a este mensaje y con gusto le atendemos.*
- **Seguimiento:** *Hola {nombre} 👋 Le saluda {asesor} de Chevrolet {sucursal}. Gracias por su tiempo. Cuando guste agendar el servicio de mantenimiento de su {unidad}, responda a este mensaje y con gusto le ofrecemos horarios disponibles en Villahermosa, Cárdenas o Comalcalco. ¡Quedamos a sus órdenes!* (las sucursales mencionadas son las de la plantilla cargada para la agencia piloto; la plantilla de su agencia puede nombrar otras).
- **Devolución de llamada por asesor:** cuando el cliente aceptó en la llamada que un asesor le llame (vea **Llamadas pendientes** en la sección 12), el mensaje posterior a la llamada es *Hola {nombre}, le saluda {asesor} de Chevrolet {sucursal}. Un asesor se comunicará con usted para confirmarle horario y precio del servicio de su {unidad}. ¡Gracias!* Nunca se usa en su lugar el mensaje neutral ni el genérico. Esta regla no aparece todavía en la pantalla de configuración: la habilita el administrador de la plataforma con su plantilla aprobada (por confirmar para su agencia); mientras no esté habilitada, el mensaje sólo sale como texto libre si la ventana de 24 horas está abierta y, si está cerrada, se omite.

> Los mensajes de **Cita registrada** y **Cita confirmada con precio** con esta redacción sólo se envían cuando la agencia tiene ambas plantillas nuevas aprobadas y cargadas; de lo contrario se siguen enviando con la redacción anterior (un solo párrafo que sí menciona el kilometraje). Una cita registrada con la redacción anterior conserva esa redacción hasta su confirmación.

#### Cómo confirmar el precio de una cita (Revisiones)

**Quién puede hacerlo:** administradores y Super Admin de forma predeterminada, siempre que la revisión de precios esté habilitada para la agencia; el administrador de la plataforma puede autorizar a otras personas por sucursal (por confirmar para su agencia).

Cuando la IA agenda por llamada una cita de Postventa, el cliente recibe primero el mensaje **Cita registrada** (sin precio). La cita queda **Pendiente de revisión por asesor** hasta que un asesor confirme el costo; entonces se envía el mensaje **Cita confirmada con precio**.

1. Abra **Calendario** y seleccione **Revisiones** (el botón muestra cuántas revisiones hay pendientes; **Todo al día** cuando no hay ninguna).
2. Seleccione la cita en la lista **Revisiones de precio pendientes**.
3. Revise los **Detalles de cita de solo lectura**: cliente, teléfono, vehículo, VIN, **Kilometraje de cartera (no intervalo de servicio)**, servicio, horario, sucursal, asesor asignado y estado. Si aparece, consulte la **Transcripción de la llamada exacta de reserva** y, cuando existe, el **Precio estimado de catálogo** (use **Confirmar el precio estimado (copiar al precio final)** para tomarlo).
4. Capture el **Precio final (MXN, impuestos incluidos)** con hasta dos decimales y revise la **Vista previa del WhatsApp final**.
5. Seleccione **Aceptar precio**. La aprobación queda registrada y el mensaje final se envía al cliente.
6. Confirme en **Entrega de WhatsApp** el estado del proveedor (**Enviado**, **Entregado**, **Leído**, **No entregado** o **Fallido**). Aprobar y entregar son pasos independientes: *La aceptación del proveedor no significa entrega.*

> Si ve **La evidencia de la llamada exacta no está disponible**, revise la advertencia antes de aprobar; no se muestra otra llamada. Si aparece **La revisión cambió o ya fue aprobada**, actualice los detalles antes de actuar. Si aparece **Faltan datos de la cita**, contacte a soporte antes de reintentar.

<!-- CAPTURA: WhatsApp > Configuración, pestaña Postventa, bloque Mensajes post-llamada / confirmación con las reglas Confirmación, Seguimiento, Neutral, Cierre, No contestó y Post-llamada -->

<!-- CAPTURA: Calendario > Revisiones, detalle de una cita Pendiente de revisión por asesor con el campo Precio final, la Vista previa del WhatsApp final y el botón Aceptar precio -->

#### Cómo activar el modo piloto de Postventa

**Para qué sirve:** probar los mensajes y llamadas de Postventa con números del personal de la agencia antes de contactar clientes reales.

Cuando el modo piloto está activo, **todos** los mensajes de WhatsApp de Postventa (recordatorios y respuestas del respondedor) llegan únicamente a los números de la lista. El resto se omite y se contabiliza, pero nunca se envía. Además, el respondedor no agenda citas a números fuera de la lista. Las llamadas de Postventa (**Llamar** e **Iniciar contacto con vencidos**) también se filtran por esta lista: sólo se marca a los números autorizados y el resto se omite por modo piloto sin realizar la llamada.

1. Localice el bloque **Modo piloto Postventa (solo lista blanca)**.
2. Active **Activar modo piloto (lista blanca)**.
3. En **Números permitidos**, capture un número por línea en formato internacional, por ejemplo \`+5299XXXXXXXX\`.
4. Seleccione **Guardar configuración de WhatsApp**.
5. Para volver al comportamiento normal, desactive el interruptor y guarde.

> Con el modo piloto activo y la lista vacía no se envía a nadie. Antes de desactivar el modo piloto, confirme con la dirección de la agencia que los mensajes ya fueron revisados: al apagarlo, los recordatorios llegarán a clientes reales.

<!-- CAPTURA: bloque Modo piloto Postventa (solo lista blanca) con el interruptor activo y el cuadro Números permitidos con un número de ejemplo enmascarado -->

![Bloque Modo piloto Postventa con interruptor activo y Números permitidos](manual-img/29-postventa-modo-piloto.png)
*Modo piloto Postventa (solo lista blanca): interruptor activo y cuadro Números permitidos con un número de ejemplo enmascarado.*

#### Cómo configurar el respondedor de IA

1. Active el interruptor del respondedor automático de IA (en la versión actual aparece como **AI auto-responder**).
2. En **Responder agent**, seleccione el agente que responderá los mensajes entrantes de Ventas. Las conversaciones de Postventa pueden atenderse con un agente distinto de departamento **Postventa**; esa asignación la realiza el administrador de la plataforma (por confirmar).
3. Seleccione **Guardar configuración de WhatsApp**.
4. Recuerde que el respondedor actúa dentro de la ventana de 24 horas y puede apagarse por conversación desde la bandeja.

#### Cómo habilitar la conexión de WhatsApp

1. Abra **WhatsApp > Configuración**.
2. Active **WhatsApp habilitado**.
3. Capture los datos proporcionados: **WABA ID**, **ID del número de WhatsApp**, **Número de WhatsApp** en formato internacional y **SID del Messaging Service de Twilio**.
4. Capture el **SID de plantilla de respaldo**.
5. Si la plantilla usa variables, capture las **Variables de plantilla (JSON)** en el formato entregado por el administrador.
6. Seleccione **Guardar configuración de WhatsApp**.
7. Espere el mensaje **Configuración de WhatsApp guardada**.

> Si aparece **Las variables de plantilla deben ser JSON válido**, revise comillas y llaves del bloque capturado.

---

## 10. Postventa

**Qué es:** el tablero de vehículos y clientes con seguimiento de servicio (la **cartera** de postventa).

**Para qué sirve:** identificar servicios vencidos, revisar historial, consultar interacciones, lanzar llamadas a uno o varios clientes y enviar los recordatorios de servicio por WhatsApp.

**Quién puede verla:** gerentes, administradores y Super Admin, siempre que Postventa esté habilitado para la agencia. El envío de recordatorios por WhatsApp está reservado a administradores y Super Admin.

![Postventa](manual-img/20-postventa.png)
*Postventa con indicadores Total, Vencidos, Contactados y Citas, filtros y tabla de vehículos.*

### Cómo funciona el seguimiento de Postventa

1. La cartera (VIN, cliente, teléfono, modelo, último servicio y asesor) se carga desde el sistema del distribuidor mediante una importación que realiza el responsable de datos de la agencia; no se actualiza sola.
2. aivoice calcula el **próximo servicio** de cada vehículo a partir del último servicio y de la **cadencia de servicio por póliza** del modelo: por ejemplo, cada 12,000 km o 12 meses para Silverado, Trax, Tracker, Cheyenne, Suburban y Tahoe, y cada 6,000 km o 6 meses para Aveo y Onix (lo que ocurra primero). Los modelos no listados usan 12,000 km o 12 meses. Una cadencia capturada manualmente en el vehículo tiene prioridad sobre la tabla, y la tabla puede ajustarse por agencia (por confirmar). Con esa cadencia se marcan los vehículos **vencidos**.
3. Una persona de la agencia inicia el contacto: por llamada (**Llamar** o **Iniciar contacto con vencidos**) o por WhatsApp (**Enviar recordatorios**). Ninguna de estas acciones ocurre de forma automática.
4. Si el cliente responde por WhatsApp, el respondedor de IA de Postventa continúa la conversación, pregunta la sucursal y ofrece horarios para el servicio. La conversación queda visible en **WhatsApp**, filtro **Postventa**, y en el expediente del vehículo.
5. La IA no da precios ni agenda diagnósticos o fallas; en esos casos indica que un asesor dará seguimiento. La cita definitiva la confirma el asesor en el sistema del distribuidor (por confirmar según la agencia).

> Los mensajes posteriores al servicio y las encuestas de satisfacción **no** los envía aivoice en la versión actual. El recordatorio por WhatsApp la mañana de la cita existe, pero está en fase de pruebas y desactivado de forma predeterminada; vea **Recordatorio el día de la cita** más abajo.

### Cómo se comporta el agente en la llamada de Postventa

El agente de voz de Postventa sigue estas reglas en cada llamada (aplican a **Llamar**, **Iniciar contacto con vencidos** y a las campañas de Postventa):

**Apertura con la ficha completa del vehículo**

1. Saluda, se presenta y confirma que habla con el cliente.
2. En cuanto el cliente confirma, dice la **ficha completa** del vehículo, sin omitir ni resumir: unidad con versión y transmisión, último servicio con kilómetros y fecha, el **servicio de póliza** que le toca con su cadencia (por ejemplo, “cada 12 meses o 12,000 kilómetros, lo que ocurra primero”) y el **costo aproximado con IVA incluido**. Después pregunta si desea agendar la cita, y espera la respuesta antes de preguntar sucursal o fecha.
3. Si el cliente lo interrumpe mientras dice la ficha, el agente termina **una sola vez** los datos que no se escucharon y vuelve a preguntar si desea agendar.
4. La cadencia que dice el agente siempre coincide con la cadencia por póliza del modelo. Si el agente intentara decir otra (por ejemplo, “cada 6 meses” para una unidad de 12), la plataforma la corrige o la omite antes de que se escuche.

**Cómo entiende las horas que pide el cliente**

| Si el cliente dice… | El agente… |
|---|---|
| “La última hora que tengan ese día”, “lo más tarde posible” | Revisa la agenda del día y sucursal elegidos y ofrece la **última hora disponible** (con la anterior como alternativa). Si la cita que ya tiene el cliente es la última hora, se lo dice en lugar de proponer otra. |
| “A primera hora”, “lo más temprano” | Ofrece la **primera hora disponible** del día. |
| Una hora y luego “más tarde” (“¿no tienen a las 11:20? me queda mejor más tarde”) | Busca horarios **posteriores a esa hora el mismo día**. Un “más tarde” nunca convierte una hora de la mañana en una de la noche (11:20 sigue siendo de la mañana). |
| “En la tarde”, “después de la mañana” | Ofrece sólo horarios **a partir de la 1:00 p. m.** Las 12:00–12:59 se consideran “del día” y sólo se ofrecen cuando ese día no tiene ningún horario a partir de la 1:00 p. m. |
| “En la mañana” | Ofrece sólo horarios antes de las 12:00. |
| Una hora concreta (“a las dos de la tarde”) | Si está disponible, la propone y pide una confirmación; si no, ofrece las dos horas más cercanas verificadas en la agenda. |

Las horas se dicen en palabras y en español correcto: la una se pronuncia **“a la una”** (nunca “a las una”), por ejemplo “a la una y cuarenta de la tarde”. El agente sólo propone horarios que verificó en la agenda de la sucursal; cuando no puede verificarla, lo dice y no inventa horarios.

**Cierre cordial**

- Cuando el cliente ya no necesita nada más, el agente se despide con **una sola frase** y termina la llamada; no vuelve a preguntar “¿le ayudo con algo más?”.
- Si se agendó una cita en esa llamada, la despedida la repite en palabras (día, hora y sucursal), avisa que la confirmación llega por WhatsApp y agradece: “Gracias por comunicarse con el Centro de Servicio Chevrolet …, ¡que tenga un excelente día!” (en agencias que no son Grupo Cruces la frase no menciona la marca).
- Si el cliente se despide primero (“gracias, hasta luego”), el agente responde con la despedida y cuelga; la llamada no se queda en silencio hasta agotar el tiempo.

### Información principal

- Indicadores: **Total** de vehículos, **Vencidos**, **Contactados** (con al menos un intento registrado) y **Citas**.
- Etiqueta de frescura de la cartera, debajo del título: **Cartera actualizada hoy**, **Cartera actualizada hace N días** o **Cartera sin importar**. En verde cuando la importación es reciente; en rojo cuando supera el umbral de días definido para la agencia.
- Búsqueda por VIN, modelo, cliente, teléfono o asesor.
- Filtro por **Todos**, **Servicio vencido** o **Próximos**.
- Filtro por **Ubicación** (sucursal de servicio o, si no existe, de compra). **Sin ubicación** agrupa los vehículos sin sucursal.
- Tabla ordenable con las columnas Cliente, Modelo, Año, Estado servicio, Teléfono, Intentos, Último contacto, VIN, Lugar de compra y Lugar de servicio.

La columna **Estado servicio** muestra una etiqueta de color: **Vencido hace N días/meses/años** (ámbar o rojo), **Vence hoy** o **Próximo: fecha** (verde).

<!-- CAPTURA: encabezado de Postventa con la etiqueta de frescura de la cartera (“Cartera actualizada hace N días”) y los tres botones Iniciar contacto con vencidos, Enviar recordatorios y Exportar -->

![Encabezado de Postventa con etiqueta de frescura y los tres botones de acción](manual-img/30-postventa-encabezado.png)
*Encabezado de Postventa: etiqueta de frescura de la cartera y los botones Iniciar contacto con vencidos, Enviar recordatorios y Exportar.*

### Cómo buscar un cliente o vehículo

1. Abra **Postventa**.
2. Escriba VIN, modelo, nombre, teléfono o asesor en el buscador.
3. Seleccione **Todos**, **Servicio vencido** o **Próximos**.
4. Si lo necesita, seleccione una **Ubicación**.
5. Seleccione el encabezado de una columna para ordenar; la flecha indica el sentido.
6. Use **Actualizar** para cargar la información más reciente.

### Cómo revisar el expediente de un vehículo

1. Seleccione cualquier fila.
2. En el panel lateral revise VIN, modelo, año, cliente, teléfono y asesor.
3. Consulte último servicio, último contacto, próximo servicio, cadencia en meses, último resultado y lugares de compra y servicio.
4. Revise **Historial de contacto**, ordenado del más reciente al más antiguo, y seleccione **Ver historial anterior** para mostrar más registros.
5. En **Interacciones > Llamadas**, abra una llamada para leer resumen y transcripción.
6. En **Interacciones > WhatsApp**, consulte la conversación de Postventa en modo de lectura.
7. Cierre el panel con la **X** o haciendo clic fuera de él.

![Panel lateral de vehículo](manual-img/21-panel-vehiculo.png)
*Panel lateral del vehículo con datos, historial de contacto e interacciones.*

### Cómo llamar a un solo cliente

1. Localice el vehículo.
2. Seleccione **Llamar** al final de la fila.
3. Seleccione el agente.
4. Marque uno o varios números salientes.
5. Seleccione **Iniciar contacto**.
6. Revise el mensaje de confirmación. El intento quedará registrado en **Historial de contacto** del vehículo.

### Cómo iniciar contacto con todos los vencidos

1. Verifique el número que aparece en el botón **Iniciar contacto con vencidos (N)**; corresponde a los vehículos con servicio vencido.
2. Seleccione el botón.
3. Seleccione el agente.
4. Marque uno o varios números salientes.
5. Seleccione **Iniciar contacto**.
6. La plataforma informará cuántos vehículos se incluyeron y cuántos no tenían teléfono.

> Si aparece **No hay vehículos con servicio vencido**, ningún vehículo de la cartera tiene el próximo servicio en el pasado.

### Qué pasa cuando el cliente no contesta (cadencia de contacto)

Cuando usted inicia el contacto y el cliente **no contesta**, la plataforma insiste sola siguiendo la **cadencia de contacto** configurada para su agencia (sección 14.5). No hace falta volver a lanzar el contacto: la automatización continúa hasta que haya **contacto, cita o baja**.

Con la cadencia acordada (valor por defecto para Grupo Cruces):

| Momento | Qué hace la plataforma |
| --- | --- |
| Llamada 1 | Marca al cliente. |
| Llamada 2 | Si no contestó, vuelve a marcar **15 minutos** después. |
| WhatsApp “no contestó” | Tras la **segunda** llamada sin respuesta envía **un solo** WhatsApp ese día. |
| Llamada 3 | Vuelve a marcar **1 hora** después de la segunda. |
| Siguiente día de contacto | Si ninguna llamada fue contestada, repite el plan **un día sí y uno no** (p. ej. lunes, miércoles y viernes; nunca en domingo) hasta completar **3 días de contacto** en la semana. |
| Último WhatsApp de la semana | Incluye el menú: *¿Sigue interesado en agendar su cita de servicio? Responda: 1) Sí · 2) Por el momento no · 3) No deseo que me contacten*. |
| Sin contacto en la semana | Pausa de **15 días** y el ciclo vuelve a empezar (ciclo 2). |
| Sin contacto tras el ciclo 2 | La automatización **se detiene** para ese servicio vencido (“no hostigar”). Vuelve a llamar sola cuando el vehículo tenga un **nuevo** servicio vencido, o cuando usted pulse **Reanudar contacto** en el vehículo. |

- Si el cliente **responde por WhatsApp** (cualquier mensaje o las opciones 1 / 2), las llamadas pendientes de la cadencia se detienen y el respondedor de Postventa continúa la conversación. Si responde **1 (Sí)** al menú, además se crea una **llamada pendiente** para un asesor en **Calendario → Llamadas pendientes**.
- Si responde **3** (o escribe *no deseo que me contacten*), el vehículo queda marcado como **Puede contactar = NO** en la cartera, con un evento “no contactar” visible en el historial de contacto (para el CRM), y no se le vuelve a llamar ni a escribir.
- El estado de la cadencia se ve en la fila del vehículo (columna **Último contacto**) y en su ficha: **No contestó · N ciclos sin contacto**, **Pausado hasta <fecha>**, **Detenido · N ciclos sin contacto**, **Opt-out**, **Contactado** o **Reanudado por <quién>**.
- **Reanudar contacto** (ficha del vehículo, sólo cuando está **Detenido**): reinicia el conteo de ciclos para ese servicio vencido y vuelve a poner en cola las llamadas detenidas (salen dentro del horario de llamadas). Queda registrado **quién** lo reanudó y **cuándo**, en la ficha y en el historial de contacto. Un cliente con **Puede contactar = NO** no se reanuda desde aquí: primero cambie la marca en el CRM.
- Las llamadas sólo salen dentro del horario de llamadas de la agencia; una llamada que caería fuera de horario se mueve al siguiente día de contacto.
- Los vehículos con **Puede contactar = NO**, vendidos, colisionados o con cliente fallecido nunca se incluyen.
- Cada campaña conserva la cadencia vigente en el momento en que se lanzó; un cambio en la configuración aplica a los contactos que se inicien después.

> Si la cadencia de su agencia está en modo **Heredado**, se conserva el comportamiento anterior (reintentos definidos por la campaña y un solo WhatsApp de “no contestó” por cliente).

### Cómo enviar recordatorios de servicio por WhatsApp

**Quién puede hacerlo:** administradores y Super Admin.

El botón **Enviar recordatorios** envía, por WhatsApp, el recordatorio que corresponde a cada vehículo vencido según las reglas de Postventa configuradas en **WhatsApp > Configuración** (sección 9.2). Antes de enviar, la plataforma siempre muestra cuántos mensajes saldrán para que usted confirme.

1. Confirme en **WhatsApp > Configuración** que las reglas de Postventa que desea usar estén activas y, si está en pruebas, que el modo piloto esté encendido.
2. En **Postventa**, seleccione **Enviar recordatorios**. El botón mostrará **Calculando…** mientras se preparan los conteos.
3. En la ventana **Confirmar envío de recordatorios**, revise:
   - **Servicio (vencidos):** recordatorios de las prioridades 1A, 1B, 2A, 2B y Franja.
   - **Informativo (60+ días):** mensajes informativos.
   - **Omitidos:** vehículos ya contactados en las últimas 24 horas, dados de baja, sin teléfono, identificados como personal de la agencia o cuya regla de Postventa está desactivada.
4. Si el total es cero, aparecerá **No hay recordatorios pendientes por enviar en este momento**; cierre la ventana.
5. Seleccione **Confirmar y enviar**. Esta acción envía mensajes reales a clientes.
6. Espere el resumen **Recordatorios enviados: N · omitidos: N · fallidos: N**. La columna **Último contacto** se actualizará.

> Un vehículo recibe como máximo un recordatorio cada 24 horas, aunque se seleccione el botón varias veces. Envíe los recordatorios en horario de atención de la agencia para que el respondedor pueda ofrecer citas del día hábil siguiente.

<!-- CAPTURA: ventana Confirmar envío de recordatorios con los conteos Servicio (vencidos), Informativo (60+ días) y Omitidos, y los botones Cancelar y Confirmar y enviar -->

![Ventana Confirmar envío de recordatorios con conteos y botones](manual-img/31-confirmar-recordatorios.png)
*Ventana Confirmar envío de recordatorios: conteos Servicio (vencidos), Informativo (60+ días) y Omitidos, con los botones Cancelar y Confirmar y enviar.*

### Recordatorio el día de la cita (en pruebas)

**Estado:** función en fase de **pruebas**. Está desactivada de forma predeterminada en todas las agencias y sólo se activa en el entorno de pruebas por indicación de Qomplement; en producción no envía nada hasta que se habilite expresamente.

**Qué hace:** la mañana de la cita (a las **9:00**, hora de la agencia, ajustable) envía por WhatsApp un recordatorio al cliente con una cita de **Postventa** confirmada que fue agendada por aivoice (llamada o WhatsApp). Opcionalmente puede enviar otro recordatorio la víspera (por defecto a las 18:00).

- Cada cita recibe **un solo** recordatorio por día de cita, aunque el proceso se ejecute varias veces. Si la cita se mueve a otro día, el recordatorio corresponde al nuevo día.
- No se envía a citas canceladas, ya iniciadas, sin teléfono o sin sucursal, ni a citas que vienen de B.PRO o del CRM del distribuidor.
- Mientras no exista una plantilla aprobada específica para el recordatorio, se usa la plantilla aprobada de **Confirmación** de Postventa (cliente, asesor, sucursal, unidad, fecha, hora y contacto). Sin una plantilla aprobada, la regla no envía nada.
- Respeta las mismas condiciones que el resto de los mensajes de Postventa: WhatsApp habilitado en la agencia y, en pruebas, el modo piloto (sección 9.2).

**Dónde se configura:** en **WhatsApp > Configuración**, regla **Recordatorio el día de la cita (y opcional la víspera)**, con la casilla de activación, **SID plantilla (día de la cita)**, **Hora de envío** y la opción **También recordar la víspera (tarde anterior)**. Esta configuración está reservada a administradores; no la active en una agencia real sin indicación de Qomplement.

### Cómo exportar Postventa

1. Abra **Postventa**.
2. Seleccione **Exportar**.
3. Guarde el archivo \`postventa.xlsx\`.

---

## 11. Analítica

**Qué es:** el tablero de resultados operativos y comerciales.

**Para qué sirve:** medir volumen de llamadas, duración, resultados de ventas o cobranza, horarios de mayor actividad y desempeño por campaña.

![Analítica](manual-img/22-analitica.png)
*Analítica con filtros de vista y periodo, indicadores y gráficas.*

### Filtros

- Vista: **General**, **Ventas** o **Cobranza**.
- Alcance: **Organización** o **Mis llamadas**, cuando corresponde.
- Periodo: últimos 7, 30 o 90 días.
- En cuentas con varias empresas, puede existir un selector adicional de empresa.

La vista sugerida depende de la configuración de la agencia, pero puede cambiarla temporalmente con los botones superiores.

### Indicadores generales

- Llamadas de hoy, semana y mes.
- Duración promedio.
- Llamadas por día.
- Horas pico.
- Resultados de llamadas.
- Llamadas por campaña.

### Indicadores de Ventas

- Citas agendadas.
- Test Drives (pruebas de manejo).
- Leads interesados.
- Callbacks (solicitudes de devolución de llamada).
- No interesados.
- Embudo de ventas.

### Indicadores de Cobranza

- Promesas de pago.
- Devoluciones de llamada programadas.
- No contactados.
- Total de llamadas.
- Distribución por resultado.

### Cómo consultar el detalle de un indicador de Ventas

1. Seleccione la vista **Ventas**.
2. Ajuste alcance y periodo.
3. Seleccione una tarjeta, por ejemplo **Citas agendadas** o **Leads interesados**.
4. Seleccione un cliente de la lista.
5. Use **Ver más** para abrir el registro relacionado.
6. Si su operación lo requiere, use **Definir resultado** para corregir la clasificación del registro: Interesado, Cita, Prueba de manejo, Devolver llamada o No interesado. Espere el mensaje **Guardado**.
7. Use **Volver a la lista** para regresar y cierre el panel al terminar.

> Si aparece **Este registro no tiene contacto vinculado**, la llamada no está asociada a un contacto de campaña y no se le puede asignar resultado manual.

> Las tarjetas sin botón son informativas. Las tarjetas de resultados de Ventas son las que permiten abrir el listado de clientes.

---

## 12. Calendario

**Qué es:** el calendario mensual de citas de la agencia. Muestra las citas creadas por los agentes de aivoice (por llamada o WhatsApp) y, cuando la agencia tiene conectada la agenda de su sistema de distribuidor (**B.PRO**), también las citas registradas ahí.

**Para qué sirve:** consultar citas por día, separarlas por departamento, sucursal y origen, y revisar el detalle de cada una. La pantalla es de consulta; no permite crear, editar ni eliminar citas manualmente.

![Calendario](manual-img/23-calendario.png)
*Calendario mensual de citas creadas por los agentes.*

### Controles del calendario

- **Ventas / Postventa:** selector de departamento en la parte superior. Cada departamento muestra su propia agenda; una cita de Postventa incluye placas y VIN, y una de Ventas el vehículo de interés.
- **Sucursal:** cuando la agencia tiene dos o más sucursales, aparece un selector con **Todas** y cada sucursal, con la cantidad de citas de cada una. La opción elegida se recuerda para su próxima visita.
- **Leyenda y filtros:** las etiquetas **B.PRO** e **IA** indican el origen de la cita; **Ventas** y **Postventa**, el departamento. Seleccione una etiqueta de la fila **Filtros** para mostrar u ocultar ese grupo.
- **Fuente de verdad: agenda B.PRO:** cuando este aviso aparece, la disponibilidad se lee en vivo de la agenda del distribuidor y los conflictos se bloquean en ambos departamentos.

> Si aparece **B.PRO no está disponible**, no se muestran horarios abiertos para evitar citas en conflicto. Las citas creadas por la IA siguen visibles.

<!-- CAPTURA: parte superior del Calendario con el selector Ventas/Postventa, el selector de Sucursal con Todas y las sucursales con su conteo, y la fila de Leyenda y Filtros (B.PRO, IA, Ventas, Postventa) -->

![Parte superior del Calendario con selector de departamento, Leyenda y Filtros](manual-img/32-calendario-encabezado.png)
*Parte superior del Calendario: selector Ventas/Postventa, aviso Fuente de verdad B.PRO y la fila de Leyenda y Filtros (B.PRO, IA, Ventas, Postventa). El selector de Sucursal aparece cuando la agencia tiene dos o más sucursales con citas.*

### Cómo consultar una cita

1. Abra **Calendario**.
2. Elija el departamento **Ventas** o **Postventa**.
3. Si aparece el selector **Sucursal**, elija **Todas** o una sucursal.
4. Use las flechas **Mes anterior** y **Mes siguiente** para cambiar de mes.
5. Seleccione un día. Los días con citas muestran la cantidad y puntos de color según el origen.
6. Revise la lista del día a la derecha o debajo del calendario. Cada cita muestra hora, origen, departamento, asesor y sucursal. Cuando no hay asesor asignado se indica **Cola de recepción**. Una cita de Ventas agendada con prueba de manejo muestra el sufijo **· Prueba de manejo** en su título (sección 6.4).
7. Seleccione una cita para abrir el panel **Cita**.
8. Revise fecha completa y duración, asesor, cliente, teléfono (con accesos directos para llamar o abrir WhatsApp), vehículo, departamento, sucursal, origen (**IA WhatsApp**, **IA llamada**, **Manual** o **B.PRO**), estado y notas o motivo. En Postventa también verá placas y VIN.
9. Si está disponible, use **Ver conversación / llamada IA** o **Ver cita en B.PRO** para abrir el registro relacionado.
10. Cierre con la **X**.

<!-- CAPTURA: panel lateral Cita de una cita de Postventa con etiquetas de origen y departamento, fecha completa, asesor, cliente, teléfono con enlaces, vehículo, placas, VIN, sucursal, origen, estado y notas -->

![Panel lateral Cita con fecha, asesor, cliente, teléfono, vehículo, sucursal, origen, estado y notas](manual-img/33-cita-panel.png)
*Panel lateral Cita: fecha y duración, asesor, cliente, teléfono con enlaces a llamada y WhatsApp, vehículo, sucursal, origen, estado y notas. En una cita de Postventa este panel muestra además placas y VIN.*

> Si aparece **Sin citas este día** pero espera ver citas, revise el departamento, la sucursal y los filtros activos.

### Llamadas pendientes (devolución de llamada por un asesor)

**Qué es:** la bandeja de clientes que pidieron que un asesor les llame porque no había horario disponible (o para confirmar precio o hablar con un asesor). Se abre con el botón **Llamadas pendientes** en la parte superior del Calendario; el número junto al botón indica cuántas solicitudes siguen sin resolver. Debajo del título, la propia pantalla lo resume: *Clientes que pidieron que un asesor les llame porque no había horario disponible. Llame, confirme un horario y márquela atendida.* El enlace **¿Qué significan los estados?** despliega la leyenda de cada estado.

**Para qué sirve:** que un asesor tome la solicitud, llame al cliente y deje constancia del resultado. Estas llamadas se atienden manualmente; aivoice no las marca por sí solo.

**Quién puede verla:** todas las personas con sesión activa ven el botón, pero la lista se limita del lado del servidor. Gerentes, administradores y Super Admin ven las solicitudes de todas las sucursales de la agencia. Un operador ve únicamente las sucursales que tiene autorizadas para esta bandeja (cuando la agencia tiene una sola sucursal, la ve de forma automática); sin autorización, la lista aparece vacía con el aviso **No hay solicitudes de devolución de llamada en tu alcance**.

#### Cómo se crea una solicitud

Las solicitudes no se capturan a mano: las genera la IA durante una llamada en estos casos.

- El cliente pide un horario que la agenda no puede verificar (por ejemplo, la tarde en una sucursal que sólo atiende por la mañana), pide confirmar un precio o pide hablar con un asesor, y acepta que un asesor se comunique con él. La bandeja guarda el motivo: **Horario no disponible**, **Confirmar precio** o **Pidió un asesor**, y, si el cliente lo dijo, la preferencia **prefiere en la tarde** o **prefiere en la mañana**.
- Una transferencia en vivo no se concreta y el cliente responde **sí** a la pregunta *¿Desea que un asesor le llame más tarde?* (vea **Transferencia en vivo y devolución de llamada** en esta misma sección).

Un mismo cliente sólo tiene una solicitud abierta por teléfono en la agencia: si vuelve a llamar y acepta de nuevo, la solicitud existente aumenta su contador de **Solicitudes** en lugar de duplicarse (se muestra, por ejemplo, **Solicitudes: 2 · última hoy 23:30**) y los plazos **Tomar antes de** / **Llamar antes de** se vuelven a calcular desde esa última solicitud.

#### Qué muestra cada solicitud

- **Cliente** y **Teléfono.** El teléfono aparece enmascarado; el número completo sólo lo ven el asesor que tiene asignada la solicitud y los gerentes o administradores (aviso **El teléfono completo se muestra al asesor asignado o a un gerente**).
- **Estado:** **Abierta** (nadie la ha tomado), **Asignada**, **Atendida**, **Sin respuesta** o **Cancelada**. La bandeja sólo lista las solicitudes **Abiertas** y **Asignadas**.
- **Sucursal** con su nombre (por ejemplo **Comalcalco**; nunca el código interno) o **Sin sucursal**, **Antigüedad** desde que se creó y el **Motivo** con la frase del cliente (**El cliente dijo**, tomada de la llamada). Si la llamada no dejó una frase utilizable, aparece **Lo que pidió el cliente** en palabras, por ejemplo *No había horario disponible; pidió que un asesor le llame · Prefiere por la tarde*.
- **Tomar antes de:** plazo sugerido para que alguien tome la solicitud, dos horas hábiles después de la última solicitud del cliente.
- **Llamar antes de:** plazo sugerido para devolver la llamada, un día hábil después de la última solicitud. Ambos plazos se calculan con el horario de atención de la agencia (zona horaria de la Ciudad de México), saltando días cerrados.
- **Vencida:** la solicitud superó el plazo que le corresponde (el de tomar, si está abierta; el de llamar, si ya está asignada). Las vencidas aparecen primero, seguidas de las más antiguas. El resumen de la lista indica **N pendientes · M vencidas**.
- **Asignada a**, **Intentos** registrados y el historial de intentos con fecha, persona y nota.

#### Cómo atender una solicitud

1. Abra **Calendario** y seleccione **Llamadas pendientes**.
2. Seleccione la solicitud. Use **Actualizar** si espera solicitudes nuevas.
3. Si usted es operador y la solicitud está **Abierta**, seleccione **Tomar esta llamada**; la solicitud pasa a **Asignada** a su nombre y se muestra el teléfono completo.
4. Si usted es gerente o administrador, puede elegir a la persona en **Asignar a** y seleccionar **Asignar**. Sólo aparecen asesores habilitados para esa sucursal; de lo contrario verá **Ese asesor no puede atender esta sucursal**.
5. Llame al cliente desde su teléfono o conmutador habitual, o use **Volver a llamar** en la pestaña **Llamada**: tras confirmar, el agente automático de postventa marca al cliente y el intento queda registrado. Después de cada llamada, escriba una **Nota del intento** (contestó, buzón, ocupado) y seleccione **Registrar intento**. Cada grupo de botones trae una línea de ayuda que explica para qué sirve.
6. Cierre la solicitud con uno de estos botones:
   - **Marcar atendida** cuando ya confirmó un horario con el cliente.
   - **Cerrar sin respuesta** cuando el cliente no contestó; requiere al menos **dos intentos registrados** (el botón queda deshabilitado antes).
   - **Cancelar solicitud** cuando ya no procede; escriba el **Motivo de la cancelación** antes de seleccionarlo.
   Los tres piden confirmación y explican qué va a pasar antes de cerrar la solicitud.
7. Seleccione **Volver a la lista** para continuar con la siguiente.

Sólo pueden registrar intentos o cerrar la solicitud el asesor asignado y los gerentes o administradores de esa sucursal. Si otra persona cambió la solicitud mientras usted la veía, aparece **La solicitud cambió; se recargó la versión actual**: revise los datos nuevos antes de repetir la acción.

> **Bandeja no configurada en este ambiente.** Si el botón muestra un guion y al abrirlo ve este aviso, el ambiente no tiene habilitada la bandeja de llamadas pendientes; no hay solicitudes que mostrar y debe solicitarlo al administrador de la plataforma. Mientras tanto, las solicitudes de clientes con VIN se reflejan como **Asesor solicitado** en la ficha del vehículo de Postventa.

> El detalle tiene dos pestañas: **Llamada** (resumen y transcripción de la llamada en la que el cliente pidió al asesor, con **Volver a llamar**) y **WhatsApp** (la conversación de postventa con ese cliente, la misma que en la bandeja de WhatsApp, con los mensajes más recientes abajo). Fuera de la ventana de 24 horas, el selector muestra el **nombre de la plantilla aprobada y una vista previa** de su texto; la etiqueta **La IA responde automáticamente · Pausar IA** indica si el auto-respondedor contesta en ese hilo y permite pausarlo.

<!-- CAPTURA: Calendario con el botón Llamadas pendientes y su contador, y la lista abierta con una solicitud Abierta, su motivo y la etiqueta Vencida -->

<!-- CAPTURA: Detalle de la solicitud con Tomar antes de, Llamar antes de, Intentos y los botones Tomar esta llamada, Registrar intento, Marcar atendida, Cerrar sin respuesta y Cancelar solicitud -->

### Transferencia en vivo y devolución de llamada

Cuando el cliente pide hablar con una persona durante una llamada con la IA, el agente intenta la **transferencia humana** configurada en el agente (número principal y **Directorio de transferencia**, vea la sección 6). En la versión en pruebas, la transferencia funciona así:

1. aivoice marca a los contactos del directorio en orden, con un tiempo total máximo de 60 segundos.
2. El asesor que contesta escucha *Llamada de un cliente que solicita un asesor. Presione uno para aceptar la llamada.* Sólo si presiona **1** se conecta con el cliente; si no presiona nada (o contesta un buzón de voz), aivoice marca al siguiente contacto.
3. Si ningún asesor acepta, o si la agencia está fuera de horario o no tiene a quién transferir, el cliente escucha *En este momento no fue posible comunicarle con un asesor. ¿Desea que un asesor le llame más tarde? Por favor responda sí o no.* Si no se entiende la respuesta, se repite una vez.
4. Con un **sí**, la solicitud queda registrada en **Llamadas pendientes** y el cliente escucha *Perfecto, quedó registrado. Un asesor le llamará lo antes posible.* (o *Ya tenemos su solicitud registrada…* si ya tenía una abierta). Con un **no**, o sin respuesta clara, no se registra nada y así se le informa.

> **En pruebas.** El filtro de **Presione uno** y la pregunta de devolución de llamada están en fase de pruebas y requieren que el ambiente tenga habilitada la bandeja de llamadas pendientes. Sin ella, la transferencia conecta al primer asesor que contesta, sin filtro, y no se registra ninguna solicitud. Si además la agencia está cerrada, el cliente escucha *Por favor comuníquese de nuevo en horario de atención* y no se promete ninguna llamada.

#### Cuando el horario pedido no se puede verificar (voz y WhatsApp)

Tanto por llamada como por WhatsApp, el agente de citas de Postventa deja de ofrecer horarios y pasa el caso a un asesor con una sola frase en estos casos: el cliente pide la **tarde** y no hay horarios verificados de tarde ese día, pide algo que el agente de citas no resuelve (garantías, quejas, facturas, cotizaciones, refacciones, financiamiento, trámites, seminuevos, etc.) o se muestra desesperado y pide una persona.

- Si pidió la tarde: *Le aviso a un asesor para que le confirme un horario de tarde.*
- En los demás casos: *Le aviso a un asesor para que le dé seguimiento y se comunique con usted.*
- Si el cliente insiste después: *Un asesor le confirmará en breve; ya tiene su solicitud.* El agente ya no vuelve a ofrecer los horarios de la mañana.

Qué queda registrado:

- **Por llamada:** si el cliente acepta, se crea la solicitud en **Llamadas pendientes** (motivo **Horario no disponible**, **Confirmar precio** o **Pidió un asesor**) y el mensaje automático de WhatsApp posterior a la llamada le confirma que un asesor se comunicará con él (vea la sección 9.2).
- **Por WhatsApp:** el vehículo queda marcado como **Asesor solicitado** en Postventa y el equipo de la sucursal recibe una notificación por WhatsApp en el número de contacto de esa sucursal (por confirmar: requiere que la sucursal tenga teléfono de equipo configurado). Estas solicitudes no aparecen todavía en **Llamadas pendientes**.

---

## 13. Teléfonos

**Qué es:** la administración de números que aivoice usa para llamadas salientes y entrantes.

**Para qué sirve:** agregar números, asignar un agente para llamadas entrantes, pausar entradas y editar etiquetas.

**Quién puede verla:** administradores y Super Admin.

![Números de Teléfono](manual-img/24-telefonos.png)
*Teléfonos: total de números, entradas activas, agente entrante y acciones.*

### Cómo agregar un número

1. Abra **Teléfonos**.
2. Seleccione **Agregar Número**.
3. Elija el país.
4. Capture el número sin repetir el código de país.
5. Escriba una etiqueta clara, por ejemplo “Ventas CDMX”.
6. Si administra varias empresas, seleccione la empresa correcta.
7. Seleccione **Agregar Número**.

### Cómo configurar llamadas entrantes

1. En la fila del número, abra el selector **Agente Entrante**.
2. Seleccione el agente que atenderá las llamadas. Espere el mensaje **Entrante configurado**. Para desactivarlo, elija **-- Desactivado --**.
3. Seleccione el ícono de engrane (**Configurar Twilio**) para aplicar la configuración telefónica, cuando sea necesario. Espere **Webhook de Twilio configurado**.
4. Use el interruptor de la fila para activar o pausar las llamadas entrantes. El indicador **Entrantes Activos** de la parte superior refleja cuántos números reciben llamadas.

### Cómo cambiar la etiqueta

1. Seleccione el ícono de lápiz.
2. Cambie la etiqueta.
3. Seleccione **Guardar**.

### Cómo eliminar un número

1. Confirme que no se utiliza en campañas ni como número entrante.
2. Seleccione el ícono de bote de basura.
3. Confirme la eliminación.

---

## 14. Equipo y configuración de la agencia

**Qué es:** la pantalla para administrar personas, invitaciones y ciertos ajustes de la organización.

**Quién puede verla:** gerentes, administradores y Super Admin. Los gerentes administran principalmente operadores; los cambios de roles superiores requieren un administrador.

![Gestión de Equipo](manual-img/25-equipo.png)
*Gestión de Equipo con indicadores, Google Calendar, ubicaciones de Postventa y miembros.*

### 14.1 Cómo invitar a una persona

1. Abra **Equipo**.
2. Seleccione **Invitar Miembro**.
3. Capture nombre y correo.
4. Seleccione el rol disponible.
5. Si administra varias empresas, seleccione la empresa.
6. Seleccione **Enviar Invitación**.
7. Si se muestra un enlace, seleccione **Copiar Enlace** y envíelo por un medio seguro como respaldo.

> Los gerentes sólo pueden invitar operadores. Los administradores pueden asignar cualquier rol.

#### Invitaciones pendientes

La sección **Invitaciones Pendientes** muestra personas que aún no completan su registro. Seleccione **Cancelar** sólo si desea invalidar esa invitación.

#### Cómo editar un miembro

1. Localice a la persona en **Miembros Activos del Equipo**.
2. Seleccione el lápiz.
3. Modifique nombre, correo, rol o empresa, según sus permisos. El cambio de rol está reservado a administradores.
4. Seleccione **Guardar Cambios**.

#### Cómo eliminar un miembro

1. Localice a la persona.
2. Seleccione el bote de basura.
3. Confirme la eliminación.

### 14.2 Google Calendar de la organización

**Para qué sirve:** permite que los agentes registren citas en un calendario compartido de Google de la organización. Es opcional; las citas siguen visibles en la pantalla **Calendario** de aivoice aunque no esté conectado.

#### Cómo conectarlo

1. En **Equipo**, localice **Google Calendar**.
2. Seleccione **Conectar Google Calendar**.
3. Inicie sesión con la cuenta autorizada de la agencia.
4. Acepte los permisos solicitados.
5. Regrese a aivoice y confirme que aparezca **Conectado como** junto al correo correcto.

> Sólo administradores pueden realizar la conexión. Si el navegador bloquea la ventana, permita ventanas emergentes e intente de nuevo.

### 14.3 Ubicaciones de Postventa

**Para qué sirve:** administra las sucursales disponibles en el filtro **Ubicación** de Postventa. El selector de sucursal del Calendario se alimenta de las sucursales configuradas para la agencia en el sistema del distribuidor, no de esta lista.

#### Cómo agregar una ubicación

1. En **Equipo**, localice **Ubicaciones de Postventa**.
2. Escriba el nombre de la sucursal.
3. Seleccione **Agregar** o presione \`Enter\`.
4. Confirme que la ubicación aparezca como etiqueta.

Para eliminarla, seleccione la **X** de su etiqueta.

### 14.4 Ajustes de empresa para Super Admin

Un Super Admin puede:

- Crear una empresa.
- Asignar nombre, industria y vista de Analítica.
- Editar la descripción de la empresa que los agentes usan como contexto.
- Cambiar la vista sugerida entre General, Ventas y Cobranza.

> Elegir **Cobranza** como vista de la empresa también oculta el módulo de Postventa para esa organización.

### 14.5 Cadencia de contacto (Postventa)

**Dónde:** **Configuración** (menú lateral) → tarjeta **Cadencia de contacto (Postventa)**.

**Quién puede editarla:** administradores y Super Admin. Los demás roles ven la cadencia vigente en modo lectura.

La tarjeta muestra la cadencia efectiva de la agencia (y de dónde viene: configurada para la agencia, acuerdo del cliente por defecto o comportamiento heredado) y permite ajustar:

| Ajuste | Significado | Valor acordado (Grupo Cruces) |
| --- | --- | --- |
| Modo | **Cadencia por agencia** o **Heredado** (comportamiento anterior) | Cadencia por agencia |
| Llamadas por día de contacto | Cuántas veces se marca el mismo día | 3 |
| Espera entre llamadas | Minutos antes de la llamada 2, antes de la llamada 3… | 15 min · 60 min |
| Cada cuántos días | 2 = un día sí y uno no | 2 |
| Días de contacto por ciclo | Días con llamadas antes de pausar (“una semana”) | 3 |
| Días permitidos | Días de la semana en que se puede marcar | Lun–Sáb |
| Pausa tras el ciclo sin contacto | Días de espera antes de reiniciar el ciclo | 15 |
| Ciclos máximos por servicio vencido | Tras N ciclos sin contacto la automatización se detiene para ese servicio vencido (0 = hasta lograr contacto, cita o baja). Vuelve con un nuevo servicio vencido o con **Reanudar contacto** en el vehículo | 2 |
| WhatsApp “no contestó” tras la llamada n.º | Después de qué llamada sin respuesta se envía el WhatsApp (una vez por día) | 2 |
| Menú de baja en el último WhatsApp | El último WhatsApp del ciclo ofrece 1) Sí · 2) Por el momento no · 3) No deseo que me contacten | Sí |

1. Ajuste los valores y seleccione **Guardar cadencia**. Los valores fuera de rango se corrigen al valor por defecto.
2. **Restablecer al valor por defecto** elimina la configuración propia y vuelve al valor por defecto de la agencia.
3. Para que el último WhatsApp lleve el menú de baja, registre la plantilla aprobada en **WhatsApp > Configuración**, regla **No contestó · último de la semana (menú de baja)**. Sin esa plantilla se envía el WhatsApp de “no contestó” normal y el registro lo indica.

> Las campañas ya lanzadas conservan la cadencia con la que se lanzaron.

---

## 15. Herramientas personalizadas

**Qué es:** una pantalla administrativa para conectar acciones externas que un agente puede usar durante una llamada.

**Disponibilidad:** la ruta existe, pero no aparece en el menú lateral actual. Sólo debe utilizarse si el administrador de la plataforma la habilita y proporciona acceso directo.

**Para qué sirve:** crear, editar, probar y eliminar conexiones que luego pueden asignarse a agentes.

> Esta sección normalmente requiere apoyo técnico. El personal de agencia no debe modificar direcciones, encabezados o parámetros sin instrucciones del responsable de integración.

![Herramientas Personalizadas](manual-img/26-herramientas.png)
*Pantalla de Herramientas Personalizadas con el botón Crear Herramienta.*

### Cómo probar una herramienta existente

1. Abra la pantalla mediante el enlace proporcionado por el administrador.
2. Localice la herramienta.
3. Seleccione **Probar webhook**.
4. Revise si la prueba fue exitosa o fallida.
5. Si falla, comparta el nombre de la herramienta y el mensaje con soporte.

### Cómo crear o editar una herramienta

1. Seleccione **Crear Herramienta** o el lápiz de una herramienta existente.
2. Complete nombre, descripción y datos entregados por el responsable de integración.
3. Agregue los campos requeridos sólo según la especificación recibida.
4. Defina si estará activa.
5. Guarde.
6. Pruebe la herramienta antes de asignarla a un agente.

---

## 16. Preguntas frecuentes

Respuestas breves a las dudas más comunes, agrupadas por tema: campañas, llamadas, WhatsApp, Postventa, Calendario y permisos.

### ¿Por qué mi campaña está en Borrador?

Porque todavía no se ha iniciado correctamente. Abra la campaña, confirme que tenga agente, número de origen y contactos válidos, y seleccione **Iniciar**. Si creó una campaña desde una integración y salió antes de lanzarla, vuelva a revisar su configuración.

### ¿Por qué mi campaña está Programada y todavía no llama?

Revise la fecha, la hora programada y el horario permitido. Las horas se interpretan con el horario de CDMX. Una campaña programada espera hasta la fecha indicada y sólo llama dentro de la franja permitida.

### ¿Por qué no salen llamadas?

Las causas más comunes son:

- El agente está inactivo.
- No se eligió un número de origen.
- La campaña está en Borrador, Programada o Pausada.
- La hora actual está fuera del horario permitido.
- No hay contactos válidos o todos ya terminaron sus intentos.
- Los teléfonos no tienen código de país o fueron rechazados en la validación.
- Su rol permite crear la campaña, pero no cambiar su estado; solicite apoyo a un gerente o administrador.

### ¿Por qué algunos contactos aparecen como inválidos?

El número puede estar vacío, incompleto, tener caracteres no reconocidos o no incluir un código de país válido. Corrija el archivo y vuelva a cargarlo.

### ¿Qué significa un teléfono compartido o duplicado?

Un duplicado exacto puede omitirse para evitar llamadas repetidas. Un teléfono compartido puede pertenecer a más de un registro o cuenta; la plataforma lo señala, pero puede conservar los registros cuando su contexto es distinto.

### ¿Por qué no puedo seleccionar Siguiente al importar contactos?

La plataforma necesita al menos un contacto válido. Revise los encabezados del archivo, en especial \`phone\`/\`telefono\`, y el detalle de contactos inválidos.

### ¿Puedo agregar contactos después de iniciar una campaña?

Sí. Abra el detalle y seleccione **Agregar leads**. La plataforma agregará los nuevos y omitirá repetidos.

### ¿Por qué no veo un agente al crear una campaña o llamada?

Puede estar inactivo, pertenecer a otro alcance o no estar disponible para su empresa. Pida a un gerente o administrador que revise la tarjeta del agente.

### ¿Por qué no aparece una transcripción?

Puede ocurrir si la llamada aún está en curso, nunca fue contestada, terminó demasiado pronto o el procesamiento sigue pendiente. Actualice después de unos minutos. Si persiste en una llamada conectada, reporte el identificador de la llamada.

### ¿Cuál es la diferencia entre Llamadas e Historial?

**Llamadas** sirve para iniciar llamadas y revisar sesiones recientes. **Historial** sirve para buscar todas las llamadas por fecha o teléfono y abrir su detalle completo.

### ¿Por qué no puedo escribir en WhatsApp?

Puede estar fuera de la ventana de 24 horas, el cliente puede haberse dado de baja o la conversación puede no tener una plantilla de respaldo. Revise el aviso que aparece sobre el área de respuesta.

### ¿Por qué sólo puedo enviar una plantilla por WhatsApp?

Cuando han pasado más de 24 horas desde el último mensaje permitido del cliente, WhatsApp exige iniciar de nuevo con una plantilla aprobada.

### ¿Por qué no aparece la pestaña Configuración en WhatsApp?

Sólo administradores y Super Admin pueden verla.

### ¿Por qué no veo Postventa?

Su rol puede no tener permiso o la empresa puede estar configurada para Cobranza. Consulte al administrador.

### ¿Por qué no veo Teléfonos?

La administración de números está reservada para administradores y Super Admin.

### ¿Por qué la campaña omitió contactos sin intentar llamarlos?

Si la agencia está en fase piloto de marcación, sólo se llama a los números autorizados por el administrador de la plataforma; los demás se omiten y se contabilizan. También se omiten los duplicados exactos. Consulte a su administrador si necesita ampliar la lista (por confirmar).

### ¿Por qué los recordatorios de Postventa no llegan a los clientes?

Revise, en este orden: que las reglas de Postventa estén activas en **WhatsApp > Configuración**; que el **modo piloto** esté apagado o que el número esté en la lista de números permitidos; que el vehículo no haya recibido un recordatorio en las últimas 24 horas; que el cliente no esté dado de baja; y que alguien haya seleccionado **Enviar recordatorios** y confirmado el envío. Los recordatorios no se envían solos.

### ¿Por qué la etiqueta de la cartera de Postventa está en rojo?

La cartera no se ha importado en los últimos días y los datos de último servicio pueden estar desactualizados. Solicite al responsable de datos de la agencia una nueva importación antes de enviar recordatorios, para evitar recordar un servicio que ya se realizó.

### ¿Por qué el botón Enviar recordatorios muestra un error de permisos?

El envío de recordatorios por WhatsApp está reservado a administradores y Super Admin. Los gerentes pueden ver Postventa y lanzar llamadas, pero no enviar recordatorios.

### ¿Puedo editar citas desde Calendario?

No en la pantalla actual. Calendario es una vista de consulta de las citas generadas por los agentes y, cuando aplica, de la agenda del distribuidor.

### ¿Por qué no veo el selector de Sucursal en Calendario?

Sólo aparece cuando la agencia tiene dos o más sucursales configuradas. Con una sola sucursal, el calendario la muestra directamente.

### ¿Qué significa “B.PRO no está disponible” en Calendario?

La agenda del distribuidor no respondió. Para evitar citas en conflicto, no se muestran horarios abiertos de esa fuente hasta que vuelva a estar disponible. Las citas creadas por la IA siguen visibles.

### ¿Por qué veo dos conversaciones de WhatsApp con la misma persona?

Ventas y Postventa usan líneas de WhatsApp distintas, por lo que un mismo cliente puede tener una conversación por departamento. Use el filtro de departamento para ubicar la correcta.

### ¿Qué diferencia hay entre Pausar y Detener una campaña?

**Pausar** permite continuar después desde el mismo avance. **Detener** finaliza la ejecución y no debe usarse como pausa temporal.

### ¿Qué hago si el Panel muestra datos viejos?

Abra nuevamente la sección o actualice la página. Para información en vivo de una campaña, use su detalle, que se actualiza periódicamente mientras está en ejecución.

---

## 17. Errores comunes y cómo resolverlos

| Mensaje o situación | Qué significa | Qué hacer |
|---|---|---|
| Correo o contraseña incorrectos | Los datos no coinciden con una cuenta activa. | Revise mayúsculas, pruebe de nuevo o restablezca la contraseña. |
| Invitación inválida o expirada | El enlace ya no es válido. | Solicite una invitación nueva. |
| Las contraseñas no coinciden | Los dos campos tienen valores diferentes. | Vuelva a capturarlos. |
| No se pudieron cargar los datos | Hubo un problema de conexión o servicio. | Revise internet, actualice la página y reintente. |
| No hay agentes activos disponibles | Ningún agente elegible está activo. | Active uno o solicite apoyo al administrador. |
| Selecciona un agente y un número | Faltan datos obligatorios para llamar. | Elija agente, destino y, cuando corresponda, número de origen. |
| Archivo procesado, pero 0 contactos válidos | No se reconocieron filas utilizables. | Revise formato, encabezados y teléfonos. |
| Formato JSON inválido | Los datos pegados no tienen el formato esperado. | Vuelva a copiar la información completa desde la fuente. |
| No hay contactos para lanzar | La campaña no recibió un archivo, lista o integración válida. | Regrese al paso de contactos y cargue al menos uno. |
| No tienes permiso para iniciar la campaña | Su rol no puede cambiar el estado. | Solicite a un gerente o administrador que la inicie. |
| Campaña fallida | La ejecución encontró un problema. | Abra la campaña, revise contactos y configuración, y comparta el error con soporte. |
| Fuera de ventana de 24h | No puede enviarse texto libre por WhatsApp. | Envíe una plantilla aprobada. |
| No hay plantilla disponible | No se configuró una plantilla de respaldo. | Solicite al administrador configurarla. |
| Lead dado de baja | El cliente no acepta mensajes de WhatsApp. | No envíe mensajes; siga el proceso de consentimiento de la agencia. |
| Error al cargar la transcripción | El detalle aún no existe o no está disponible. | Espere unos minutos, actualice y vuelva a intentar. |
| Acceso denegado | Su cuenta no tiene permiso sobre ese registro o módulo. | Confirme que está en la empresa correcta y contacte al administrador. |
| Micrófono no disponible | El navegador no tiene permiso o eligió otro dispositivo. | Autorice el micrófono, seleccione el correcto y reinicie la llamada web. |
| No hay números disponibles | No existen números de origen configurados para la empresa. | Solicite a un administrador agregarlos en Teléfonos. |
| Las variables de plantilla deben ser JSON válido | El bloque de variables de la plantilla de respaldo tiene un error de formato. | Revise comillas y llaves; pida al responsable de WhatsApp el bloque exacto. |
| WhatsApp automático no está habilitado para este tenant | La agencia no tiene activa la conexión de WhatsApp o los mensajes automáticos. | Un administrador debe habilitar WhatsApp y las reglas en **WhatsApp > Configuración**. |
| No hay recordatorios pendientes por enviar en este momento | Ningún vehículo vencido cumple las condiciones de envío (regla activa, sin contacto en 24 h, con teléfono, fuera del piloto). | Revise la sección 10 y la pregunta frecuente sobre recordatorios; cierre la ventana. |
| No hay vehículos con servicio vencido | Ningún vehículo de la cartera tiene el próximo servicio en el pasado. | Revise la etiqueta de frescura de la cartera; puede requerir una nueva importación. |
| B.PRO no está disponible | La agenda del distribuidor no respondió al Calendario. | Espere y actualice; las citas creadas por la IA siguen visibles. |
| Algo salió mal / pantalla de error | Ocurrió un error inesperado de la página. | Seleccione **Recargar**. Si continúa, tome una captura y contacte a soporte. |

### Información útil al reportar un problema

Incluya:

1. Nombre de la empresa.
2. Su correo y rol, sin compartir la contraseña.
3. Nombre de la campaña o agente.
4. Teléfono afectado, ocultando dígitos si la política de la agencia lo exige.
5. Fecha y hora aproximadas.
6. Identificador de llamada o campaña, si aparece.
7. Captura del mensaje de error.
8. Acción que realizó justo antes del problema.

---

## 18. Glosario breve

| Término | Significado sencillo |
|---|---|
| Agente | Asistente de voz o WhatsApp configurado para conversar con clientes. |
| Agente hijo | Agente que hereda configuración de un agente principal. |
| Campaña | Grupo de contactos y reglas para realizar llamadas salientes. |
| Contacto o lead | Persona a la que se busca atender o dar seguimiento. |
| Número de origen | Número que usa aivoice para realizar la llamada. |
| Concurrencia | Cantidad de llamadas que pueden ocurrir al mismo tiempo. |
| Reintento | Nuevo intento de llamada después de no lograr contacto. |
| Resultado | Clasificación final de la llamada, como interesado, cita o no contesta. |
| Transcripción | Texto de lo que dijeron el cliente y el agente. |
| Ventana de 24 horas | Periodo de WhatsApp en el que se permite responder con texto libre. |
| Plantilla | Mensaje previamente aprobado para iniciar o reabrir una conversación de WhatsApp. |
| VIN | Número de identificación único de un vehículo. |
| Postventa | Seguimiento de servicios, mantenimiento y reactivación de clientes. |
| Cartera | Lista de vehículos y clientes de Postventa importada desde el sistema del distribuidor. |
| Recordatorio de servicio | Mensaje de WhatsApp con plantilla aprobada que avisa al cliente que su servicio está vencido. |
| Modo piloto / lista blanca | Modalidad de prueba en la que los mensajes de Postventa sólo llegan a los números autorizados. |
| Sucursal | Ubicación física de la agencia donde se agenda o realiza el servicio. |
| B.PRO | Sistema de agenda del distribuidor cuyas citas pueden verse en el Calendario de aivoice. |

---

**Fin del manual**
`;
